import {
  AutocompleteOption,
  Categorization,
  Cell,
  Column,
  PreviousPeriodMeta,
  PreviousPeriodPayload,
  PreviousPeriodSourceRow,
  Row,
  Suggestion,
} from '../models/models';
import * as math from 'mathjs';
import { RowSelectionActionType } from './actions';
import {
  BALANCE_SHEET,
  DYNAMIC_ADJUSTMENT_STATEMENT, DYNAMIC_ADJUSTMENT_STATEMENT_ITEM_TYPES,
  INCOME_STATEMENT,
  STANDARD_ITEM_IDS,
  UNLABELED_ROW_PREFIX
} from '../../../../utils/constants';
import { ReviewQueueItem } from '../../../../models/review-queue-item';
import { DocumentFile } from '../../../../models/document-file';
import { Statement } from '../../review/human-validation/models/statement';
import { v4 as uuid } from 'uuid'
import {TemplateItem} from "@models/template-item";
import { SpreadingTemplate } from '@models/spreading-template';
import {RoundingService, ScaleType} from '@services/rounding.service';
import {
  SubmissionRuleEvaluationResult,
  SubmissionRulesService,
  TemplateSubmissionRulesByRef
} from "@services/submission-rules.service";
import {SubmissionRule, SubmissionRuleActions} from "@services/template-data.service";

export interface SubmissionRuleResultSummary {
  lineItemLabel: string;
  lineItemRef: string;
  action: SubmissionRuleActions;
  message: string;
  value: string;
}

export interface SubmissionRuleResultsSummary {
  [periodDate: string]: SubmissionRuleResultSummary[];
}

class DeferredCalculationError extends Error {}

const MAX_RECALC_ATTEMPTS = 4;

// Appended to a linked offset row's label, derived from its primary
// adjustment row's label (CITI-1274). e.g. "Test Item" -> "Test Item (Offset)".
export const OFFSET_LABEL_SUFFIX = ' (Offset)';

/**
 * Appends an occurrence index to `base`, counting how many times `base` has
 * already been seen. Labels are not unique — several document items with the
 * same label can be categorized to one template item — so previous-period
 * matching pairs the Nth prior row carrying a label with the Nth current row
 * carrying it. `counts` is caller-owned so each pass numbers from zero.
 */
function nextOccurrenceKey(counts: Map<string, number>, base: string): string {
  const occurrence = counts.get(base) ?? 0;
  counts.set(base, occurrence + 1);
  return `${base}::${occurrence}`;
}

/** Occurrence-qualified identity for a source row within its template line item. */
function nextSourceRowKey(counts: Map<string, number>, lineItemId: number, label?: string): string {
  return nextOccurrenceKey(counts, `${lineItemId}::${(label || '').trim().toLowerCase()}`);
}

function isDasAddBackRow(row: Row): boolean {
  return ['AdjustedStandardItem', 'NonAdjustableStandardItem'].includes(row?.itemClass);
}

function isDasCalculatedRow(row: Row): boolean {
  return ['GenericCalculatedItem', 'CalculatedItem'].includes(row?.itemClass);
}


function extractRollups(item: Row): Array<AutocompleteOption> {
  let options: Array<AutocompleteOption> = [];

  if (item.rollupName) {
      options.push({
          'id': item.lineItemId,
          'value': item.rollupName
      });
  } else if (item.calculated) {
    options.push({
      'id': item.lineItemId,
      'value': item.userFacingLabel || item.label,
    });
  }

  if (item.children) {
      options = options.concat.apply(options, (item.children.map(c => extractRollups(c))));
  }

  return options;
}

function extractAutocompleteOptions(item: Row): Array<AutocompleteOption> {
  let options: Array<AutocompleteOption> = [];

  if (item.isMappable && !item.calculated) {
      options.push({
          'id': item.lineItemId,
          'value': item.userFacingLabel ? item.userFacingLabel : item.label,
      });
  }

  if (item.children) {
      options = options.concat.apply(options, item.children.map(c => extractAutocompleteOptions(c)));
  }
  return options;
}

function getAutocompleteOptions(rows: Array<Row>): Array<AutocompleteOption> {
  return [].concat.apply([], rows.map(r => extractAutocompleteOptions(r)));
}

function getRollupOptions(rows: Array<Row>): Array<AutocompleteOption> {
  return [].concat.apply([], rows.map(r => extractRollups(r)));
}

/**
 * Recursively remove the source row from the any descendant of
 * the template row.
 * @param templateRow
 * @param sourceRow
 * @param allowHeaderRemoval When true, also remove items directly under headers (used for uncategorizing)
 */
function searchAndDestroy(templateRow: Row, sourceRow: Row, allowHeaderRemoval: boolean = false) {
  if (templateRow.validationRow && templateRow.validationRow.uuid === sourceRow.uuid) {
    templateRow.validationRow = null;
    return;
  }

  if (templateRow.children) {
    if (!templateRow.header || allowHeaderRemoval) {
      const idx = templateRow.children.findIndex(child => child.uuid === sourceRow.uuid);
      if (idx >= 0) {
        templateRow.children.splice(idx, 1);
        if (templateRow.calculated && templateRow.children.length === 0) {
          delete templateRow.children;
        }
        return;
      }
    }
    for (let i = 0; i < templateRow.children.length; i++) {
      searchAndDestroy(templateRow.children[i], sourceRow);
      searchAndDestroy(templateRow.children[i], sourceRow, allowHeaderRemoval);
    }
  }
}

/**
 * Search the tree for the line item with the given ID and add the
 * source row to that template row if found.
 *
 * @param templateRow The row to search
 * @param normalizedItemId The lineItemId of the template item you want to add to
 * @param isValidation Whether the item should be added as a validation for (true)
 * or component of (false) the template row
 * @param sourceRow The source row you want to categorize
 * @param path The path of rows traversed so far
 * @param originalNormalizedItemId When uncategorizing (normalizedItemId === 0), this is the original categorizedTo value to use for recursive searches
 */
function searchAndAdd(templateRow: Row, normalizedItemId: number, isValidation = false, sourceRow: Row, path: Array<Row>, originalNormalizedItemId?: number) {
  if (templateRow.lineItemId === normalizedItemId) {
    if (isValidation) {
      templateRow.validationRow = sourceRow;
    } else {
      if (!templateRow.children) {
        templateRow.children = [];
      }

      templateRow.children.push(sourceRow);
    }
    return [true, path];
  } else if (templateRow.template && originalNormalizedItemId && templateRow.children && templateRow.children.length > 0) {
    if (templateRow.children.find(child => child.categorizedTo === originalNormalizedItemId)) {
      return [true, path];
    }
  }

  if (templateRow.children) {
    for (let i = 0; i < templateRow.children.length; i++) {
      const res = searchAndAdd(templateRow.children[i], normalizedItemId, isValidation, sourceRow, path.concat([templateRow.children[i]]), originalNormalizedItemId);
      if (res[0]) {
        return res;
      }
    }
  }

  return [false, []];
}

function getMapByID(rows: Array<Row>): Map<number, Row> {
  const map = new Map<number, Row>();

  rows.forEach(r => {
    map.set(r.lineItemId, r);
    if (r.children) {
      const nested = getMapByID(r.children);
      nested.forEach((value: Row, key: number) => {
        map.set(key, value);
      });
    }
  });
  return map;
}


/**
 * This class is the datastore for the spreading screen. It is separated
 * from the component for the purposes of testing and separation of concerns.
 */
export class SpreadingState {

  static newFromStatement(statement: Statement, template: Array<Row>): SpreadingState {
    const state = new SpreadingState(new RoundingService(), new SubmissionRulesService());
    state.initializeTemplate(template);
    state.setStatement(statement);
    return state;
  }

  private static createCellFromValue(cellValue: number): Cell {
    return {
      // commentFromSpreading: '',
      // Value is read-only and figured out from rawText
      rawText: (cellValue * -1).toString(),
      // For latency compensation
      value: (cellValue * -1),
      sourceComments: []
    }
  }

  templateMap: Map<number, Row>;
  dasRefSnapshot: any = {};
  stdFrameRefSnapshot: any = {};
  // These are the dynamically calculated attributes that drive the interface.
  rows: Array<Row> = [];   // These are template rows
  previewRows: Array<Row> = [];
  sourceRows: Array<Row> = [];  // Need this
  columns: Array<Column> = [];
  autocompleteOptions: Array<AutocompleteOption> = [];
  rollupOptions: Array<AutocompleteOption> = [];
  referenceRows: Array<Row>;
  shiftDirection: 'up'|'down'|'neutral' = 'neutral';

  // Store the tree traversal path for the last categorization
  lastCategorizationPath: Array<Row> = [];

  lastSelectionAction: RowSelectionActionType;

  statementType: string = INCOME_STATEMENT;
  _calculatorRef = {};


  // Just storage for additional data
  documentFile: DocumentFile = null;
  reviewQueueItem: ReviewQueueItem = null;

  spreadingTemplateId: number = null;
  taxonomy: Array<TemplateItem> = null;

  statement: Statement = null;

  // Calculated in background on init and used for goal seek calculations
  goalSeekRollupMap: Map<number, Map<number, number>>;
  // at least this % of source rows must be categorized to trigger goal seek
  goalSeekRowCategorizationPercent = 0.6;
  clipboard: Array<any> = [];

  goalSeekCount: number = 0;
  selectedTemplateInfo: SpreadingTemplate = null;
  submissionRules: TemplateSubmissionRulesByRef = {};
  templateHasSubmissionRules: boolean = false;
  submissionRuleResultsSummary: SubmissionRuleResultsSummary = {}; // Used to display results on DAS tabs
  hasSubmissionRuleResults: boolean = false;
  submissionRuleResultsStatementLevelStatus: SubmissionRuleActions | 'PASSING' = 'PASSING';

  // -- Previous periods in data categorization --
  // Metadata for every prior period the user is allowed to display.
  availablePreviousPeriods: Array<PreviousPeriodMeta> = [];
  // PeriodKey of the auto-selected reference period (most recent matching
  // the active column's reporting_interval + scenario, falling back to
  // the most recent of any type). Null if no candidates exist.
  defaultPreviousPeriodKey: string | null = null;
  // PeriodKeys the user currently has selected (persisted server-side per
  // (user, document_file)).
  selectedPreviousPeriodKeys: Array<string> = [];
  // Resolved payloads keyed by periodKey, populated lazily as selections apply.
  previousPeriodPayloads: Map<string, PreviousPeriodPayload> = new Map();
  // Computed in display order (oldest -> newest). Read by the template
  // table view. Each entry has isPreviousPeriod=true and previousPeriodKey set.
  previousPeriodColumns: Array<Column> = [];
  // DAS tabs only: synthetic read-only rows for prior DAS rows with no
  // current-spread match, keyed by the uuid of the current source row they
  // render after. Held outside sourceRows so saves, DAS calculations, and
  // submission-rule evaluation never see them.
  dasPreviousOnlyRowsByAnchor: { [anchorUuid: string]: Array<Row> } = {};

  constructor(
    private _roundingService: RoundingService,
    private _submissionRulesService: SubmissionRulesService) {
  }

  setStatement(statement: Statement) {
    this.statement = statement;
    this.columns = statement.columns;
    this.sourceRows = statement.rows;
    this.spreadingTemplateId = statement.spreadingTemplateId;
    this.statementType = statement.statementType;
    this.resetRows(this.rows);
    const categorizations = [];
    this.sourceRows.forEach(r => {
      if (r?.weightDecimal) {
        r.weightPercentage = r.weightDecimal * 100;
      }

      if(r.itemClass==='supplementalItem'){
        r.labelIsLocked = true;
      }

      r.rollupCoefficient = 1;
      if (r.categorizedTo > 0) {
        const match = this.templateMap.get(r.categorizedTo);
        if (!match) {
          // this thing does not exist in the template
          return;
        }
        let label = '';
        if (match.rollupName && r.categorizationType === 'validation') {
          label = match.userFacingLabel ? match.userFacingLabel : match.rollupName;
        } else {
          label = match.userFacingLabel ? match.userFacingLabel : match.label;
        }
        r.categorizationLabel = label;

        const categorization: Categorization = {
          'sourceRow': r,
          'templateLineItemId': r.categorizedTo,
          'templateLineItemLabel': r.categorizationLabel,
          'isValidation': r.categorizationType === 'validation'
        }
        categorizations.push(categorization);
      }
    });

    this.categorizeRows(categorizations, false, true);

    this.deselectAll('column');
    if (this.columns.length) {
      this.columns[this.columns.length - 1].selected = true;
    }

    this.lastCategorizationPath = [];
    this.setReferenceRows();
  }

  initializeTemplate(template: Array<Row>) {
    // If we share the template amongst different statements within the same context (same cache), we would have
    // rows categorized from "Elsewhere" that make it into the autocomplete options. When we initialize the template
    // we need to totally clear out any non-template items, and then the next step is to categorize those items
    // according to whatever already exists on the statement.
    this.resetRows(template);
    this.rows = template;
    this.templateMap = getMapByID(template);
    this.autocompleteOptions = getAutocompleteOptions(template);
    this.rollupOptions = getRollupOptions(template);
    this.populateGoalSeekRollupMap();
  }
  /**
   * Categorize all selected rows to the given template item
   * @param args
   */
  categorizeAllSelectedRows(args: Categorization) {
    const index = this.getLastSelectedItemIndex('row');
    this.categorizeRows(this.getAllSelectedRows().map(r => {
      return {
        'templateLineItemId': args.templateLineItemId,
        'isValidation': args.isValidation,
        'templateLineItemLabel': args.templateLineItemLabel,
        'sourceRow': r
      }
    }),true,false);

    // Select the one after the last one we mapped
    if (index < this.sourceRows.length) {
      this.rowSelectionAction(RowSelectionActionType.SELECT_NEXT);
    } else {
      this.deselectAll();
    }

    // Set the reference rows
  }

  setReferenceRows() {
    let refs = [];
    const alwaysOnTop = [];
    if (this.statementType === INCOME_STATEMENT) {
      // Net Income
      alwaysOnTop.push(STANDARD_ITEM_IDS.NET_INCOME);
      // EBITDA
      alwaysOnTop.push(STANDARD_ITEM_IDS.EBITDA);
    } else {
      // Balance
      alwaysOnTop.push(STANDARD_ITEM_IDS.BALANCE_CHECK);
    }

    refs = this.rows.filter((r) => alwaysOnTop.indexOf(r.lineItemId) > -1);

    // Add the tree traversal for the last categorization
    this.lastCategorizationPath.forEach(r => {
      if (!refs.includes(r)) {
        refs.push(r);
      }
    });
    this.referenceRows = refs;
  }

  getSelectedRowSum(): number {
    const selectedColumnIdx = this.getLastSelectedItemIndex('column');
    if (selectedColumnIdx < 0) {
      return 0.0;
    }

    const rows = this.getAllSelectedRows();

    return rows.reduce((sum, row) => {
      return sum + this.getCellValueForCalculations(row, row.cells[selectedColumnIdx]);
    }, 0);
  }

  addRowsAtIndex(index: number, numberOfItemsToAdd = 1): void {
    const itemsToAdd = Array
      .from({length: numberOfItemsToAdd})
      .map(_ => this.emptyRow());

    if (this.sourceRows.length >= index) {
      this.sourceRows.splice.apply(
        this.sourceRows,
        [index, 0, ...itemsToAdd]
      );
    } else {
      this.sourceRows = this.sourceRows.concat(itemsToAdd);
    }
  }

  /**
   * Insert index for a new manual row categorized to `templateLineItemId`:
   * - 0 if no existing non-header row is categorized to that line item (prepend).
   * - after the last such row in `sourceRows` otherwise.
   */
  private findInsertIndexForManualCategorizedChild(templateLineItemId: number): number {
    for (let i = this.sourceRows.length - 1; i >= 0; i--) {
      const r = this.sourceRows[i];
      if (r.header) {
        continue;
      }
      if (r.categorizedTo === templateLineItemId) {
        return i + 1;
      }
    }
    return 0;
  }

  /**
   * Inserts a manual source row, categorized as a validation for (`isValidation`) or a component
   * child of the template line item (Insert Item from add-categorization modal — Manual Item view).
   * Placement: first row for that template item is prepended; otherwise inserted below the last
   * statement row already categorized to the same template line item.
   */
  insertManualCategorizedChild(
    templateLineItemId: number,
    templateLineItemLabel: string,
    isValidation = false
  ): Row {
    const insertIndex = this.findInsertIndexForManualCategorizedChild(templateLineItemId);
    const newRow = this.emptyRow();
    newRow.userFacingLabel = '';
    this.sourceRows.splice(insertIndex, 0, newRow);
    this.statement.rows = this.sourceRows;
    this.categorizeRows(
      [
        {
          sourceRow: newRow,
          templateLineItemId,
          templateLineItemLabel,
          isValidation,
        },
      ],
      true,
      true
    );
    return newRow;
  }

  /**
   * Inserts an Offset Adjustment pair (CITI-1274): a primary manual
   * adjustment row categorized to `primaryTemplateLineItemId`, plus a linked,
   * inverted offset row categorized to `offsetTemplateLineItemId`. Both rows
   * start blank and share an `offsetGroupId`. Returns the primary row so the
   * caller can scroll to it and autofocus its label.
   */
  insertOffsetAdjustmentPair(
    primaryTemplateLineItemId: number,
    offsetTemplateLineItemId: number,
    offsetTemplateLabel: string
  ): Row {
    const groupId = uuid();

    const primaryRow = this.emptyRow();
    primaryRow.userFacingLabel = '';
    primaryRow.offsetGroupId = groupId;
    primaryRow.offsetRole = 'primary';
    this.sourceRows.splice(
      this.findInsertIndexForManualCategorizedChild(primaryTemplateLineItemId),
      0,
      primaryRow
    );

    const offsetRow = this.emptyRow();
    offsetRow.userFacingLabel = '';
    offsetRow.offsetGroupId = groupId;
    offsetRow.offsetRole = 'offset';
    this.sourceRows.splice(
      this.findInsertIndexForManualCategorizedChild(offsetTemplateLineItemId),
      0,
      offsetRow
    );

    this.statement.rows = this.sourceRows;
    this.categorizeRows(
      [
        {
          sourceRow: primaryRow,
          templateLineItemId: primaryTemplateLineItemId,
          templateLineItemLabel: '',
          isValidation: false,
        },
        {
          sourceRow: offsetRow,
          templateLineItemId: offsetTemplateLineItemId,
          templateLineItemLabel: offsetTemplateLabel,
          isValidation: false,
        },
      ],
      true,
      true
    );
    return primaryRow;
  }

  /**
   * The other row in an offset pair (the offset for a primary, or vice versa).
   * Returns undefined when `row` is not part of an offset pair.
   */
  getOffsetCounterpart(row: Row): Row | undefined {
    if (!row?.offsetGroupId || !row.offsetRole) {
      return undefined;
    }
    return this.sourceRows.find(
      (r) =>
        r !== row &&
        r.offsetGroupId === row.offsetGroupId &&
        r.offsetRole !== row.offsetRole
    );
  }

  /** True for the derived (locked) offset side of an offset pair. */
  isDerivedOffsetRow(row: Row): boolean {
    return row?.offsetRole === 'offset' && !!row.offsetGroupId;
  }

  /**
   * Keeps a linked offset row's label in sync with its primary adjustment
   * row, appending the offset marker. No-op unless `primaryRow` is the
   * primary side of a pair. Returns the updated offset row, if any.
   */
  applyOffsetLabelSync(primaryRow: Row): Row | undefined {
    if (primaryRow?.offsetRole !== 'primary') {
      return undefined;
    }
    const counterpart = this.getOffsetCounterpart(primaryRow);
    if (!counterpart) {
      return undefined;
    }
    const baseLabel = primaryRow.label ?? '';
    counterpart.label = baseLabel ? `${baseLabel}${OFFSET_LABEL_SUFFIX}` : '';
    counterpart.translatedLabel = null;
    counterpart.labelEditedByUser = true;
    return counterpart;
  }

  /**
   * Mirrors a primary adjustment cell into its linked offset row as an
   * equal-and-opposite value. Reads the primary cell's already-finalized
   * value/raw text and negates it. No-op unless `primaryRow` is the primary
   * side of a pair. Returns the offset row that changed, if any.
   */
  mirrorOffsetCellValue(primaryRow: Row, cellIdx: number): Row | undefined {
    if (primaryRow?.offsetRole !== 'primary') {
      return undefined;
    }
    const counterpart = this.getOffsetCounterpart(primaryRow);
    const primaryCell = primaryRow.cells?.[cellIdx];
    const offsetCell = counterpart?.cells?.[cellIdx];
    if (!counterpart || !primaryCell || !offsetCell) {
      return undefined;
    }
    if (primaryCell.value == null || isNaN(primaryCell.value)) {
      offsetCell.value = NaN;
      offsetCell.rawText = primaryCell.rawText ?? '';
      offsetCell.roundedRawText = primaryCell.roundedRawText ?? '';
      offsetCell.confidence = 0.0;
    } else {
      const inverted = primaryCell.value * -1;
      offsetCell.value = inverted;
      offsetCell.rawText = inverted.toString();
      const roundedPrimary = parseFloat(primaryCell.roundedRawText ?? '');
      offsetCell.roundedRawText = isNaN(roundedPrimary)
        ? inverted.toString()
        : (roundedPrimary * -1).toString();
      offsetCell.confidence = 1.0;
    }
    return counterpart;
  }

  addAdjustmentForRow(rowIdx: number, adjustmentLabel: string): void {
    const row = this.sourceRows[rowIdx];
    const newRow: Row = {
      label: adjustmentLabel,
      rollupCoefficient: 1,
      footnotes: [],
      uuid: uuid(),
      cells: row.cells.map(c => {
        const cellValue = this.getCellValueForCalculations(row, c);
        return SpreadingState.createCellFromValue(cellValue);
      })
    };

    this.sourceRows.splice(rowIdx + 1, 0, newRow);
  }

  replaceRowAtIndex(row: Row, index) {
    var newRow = JSON.parse(JSON.stringify(row))

    // if we are replacing a row that is categorized we need to uncategorize it before replacing
    if (this.sourceRows[index]) {
      this.categorizeRows([{
        sourceRow: this.sourceRows[index],
        isValidation: false,
        templateLineItemId: 0,
        templateLineItemLabel: '',
      }],true,false);
      if (this.statement?.uniqueDasName) {
        // If this is a DAS tab, only paste over the label and cell values
        const replaceRow = JSON.parse(JSON.stringify(this.sourceRows[index]));
        // Do not paste over non-editable rows
        if ((
          replaceRow.itemClass === 'AdjustedStandardItem'
          || replaceRow.itemClass === 'NonAdjustableStandardItem'
          || replaceRow?.dynamicAdjustmentStatementItemType == 'asReported'
          || replaceRow?.dynamicAdjustmentStatementItemType == 'customCalculation')
        ) {
          return
        }
        replaceRow.label = newRow.label
        replaceRow.translatedLabel = newRow.translatedLabel
        replaceRow.cells = newRow.cells
        newRow = replaceRow
      }

      // Keep the existing row's uuid instead of overriding it with copied row's uuid
      if (this.sourceRows[index] && this.sourceRows[index].uuid) {
        newRow.uuid = this.sourceRows[index].uuid;
      }

      if (this.sourceRows[index].itemClass=='supplementalItem') {
        // If this is a Supp item just paste amount values
        const replaceRow = JSON.parse(JSON.stringify(this.sourceRows[index]));
        replaceRow.cells = newRow.cells
        newRow = replaceRow

        this.categorizeRows([{
          sourceRow: newRow,
          isValidation: newRow.categorizationType === 'validation',
          templateLineItemId: newRow.categorizedTo,
          templateLineItemLabel: newRow.categorizationLabel,
        }],true,true);
      } else if (newRow.itemClass === 'supplementalItem') {
        // If the pasted row is a supplemental item (but target is not), re-categorize it
        if (newRow.categorizedTo && newRow.categorizedTo > 0) {
          this.categorizeRows([{
            sourceRow: newRow,
            isValidation: newRow.categorizationType === 'validation',
            templateLineItemId: newRow.categorizedTo,
            templateLineItemLabel: newRow.categorizationLabel || '',
          }], true, true); // Use supplementalItemCatOverride: true for supplemental items
        }
      } else if (!this.statement?.uniqueDasName) {
        // For non-supplemental items, re-categorize to add back to template tree structure
        if (newRow.categorizedTo && newRow.categorizedTo > 0) {
          // If the pasted row is categorized, re-categorize it to add it back to the template tree structure
          // This ensures it's included in calculations and the scope is properly updated when evaluateRowFormula runs
          this.categorizeRows([{
            sourceRow: newRow,
            isValidation: newRow.categorizationType === 'validation',
            templateLineItemId: newRow.categorizedTo,
            templateLineItemLabel: newRow.categorizationLabel || '',
          }], true, false);
        }
      }
    }

    this.sourceRows.splice(index, 1, newRow)
  }

  replaceColumnAtIndex(col, colIndex, clipboardIndex, clipboard) {
    const newCol = JSON.parse(JSON.stringify(col['column']));
    this.columns.splice(colIndex, 1, newCol);

    this.sourceRows.forEach((row, idx) => {
      const cellData = clipboard[clipboardIndex]['rows'][idx];
      if (cellData) {
        row.cells.splice(colIndex, 1, cellData);
      } else {
        console.warn(`No clipboard data for row index: ${idx}, label: ${row.label}`);
      }
    });
  }

  deleteRowAtIndex(idx: number) {
    const row = this.sourceRows[idx];
    // Offset pairs are deleted together (CITI-1274).
    const counterpart = this.getOffsetCounterpart(row);
    this.categorizeRows([{
      sourceRow: row,
      isValidation: false,
      templateLineItemId: 0,
      templateLineItemLabel: '',
    }],true,true);
    this.sourceRows.splice(idx, 1);

    if (counterpart) {
      const counterpartIdx = this.sourceRows.indexOf(counterpart);
      if (counterpartIdx >= 0) {
        this.categorizeRows([{
          sourceRow: counterpart,
          isValidation: false,
          templateLineItemId: 0,
          templateLineItemLabel: '',
        }],true,true);
        this.sourceRows.splice(counterpartIdx, 1);
      }
    }
  }

  deleteSelectedRows() {
    // Deleting one half of an offset pair removes the other half too (CITI-1274).
    const selected = this.sourceRows.filter(r => r.selected);
    const toRemove = new Set<Row>(selected);
    selected.forEach(r => {
      const counterpart = this.getOffsetCounterpart(r);
      if (counterpart) {
        toRemove.add(counterpart);
      }
    });

    // Clear the categorizations first so we don't have any orphans.
    this.categorizeRows(Array.from(toRemove).map(r => {
      return {
        sourceRow: r,
        isValidation: false,
        templateLineItemId: 0,
        templateLineItemLabel: '',
      };
    }),true,true);

    this.sourceRows = this.sourceRows.filter(r => !toRemove.has(r));
  }

  emptyRow(): Row {
    return {
      invertValue: false,
      rollupCoefficient: 1.0,
      label: '',
      uuid: uuid(),
      footnotes: [],
      cells: this.columns.map(_ => this.emptyCell()),
      isUserAddedRow: true,
    };
  }

  emptyColumn() {
    // We need a fallback if there are no columns left
    let fye = '12/31/2000';
    if (this.columns.length) {
      fye = this.columns[0].fiscalYearEnd;
    }
    return {
      'statementDate': '',
      'preparationType': 'UNKNOWN',
      'reportingInterval': 'MONTHLY',
      'scenario': '',
      'fiscalYearEnd': fye,
    };
  }

  emptyCell(): Cell {
    return {
      rawText: '0.0',
      roundedRawText: "0.0",
      value: 0.0,
      sourceComments: []
    };
  }

  addColumnsAtIndex(idx: number, n = 1) {
    const columnsToAdd = Array.from({length: n}).map(_ => this.emptyColumn());
    if (this.columns.length >= idx) {
      // @ts-ignore
      const args = [idx, 0].concat(columnsToAdd);
      this.columns.splice.apply(this.columns, args);
    } else {
      this.columns = this.columns.concat(columnsToAdd);
    }

    this.statement.columns = this.columns;

    this.sourceRows.forEach(r => {
      const cellsToAdd = Array.from({length: n}).map(_ => this.emptyCell());
      // @ts-ignore
      r.cells.splice.apply(r.cells, [idx, 0].concat(cellsToAdd));
    });

    // Now recalculate. calculate calls initializeRow() for each row which adds the proper number of cells based on the columns.
    this.calculate();
  }

  deleteColumnAtIndex(idx: number) {
    this.columns.splice(idx, 1);
    this.sourceRows.forEach(r => {
      r.cells.splice(idx, 1);
    });

    // Always have at least one column
    if (this.columns.length === 0) {
      this.addColumnsAtIndex(0);
      return;
    }

    this.statement.columns = this.columns;
    this.calculate();
  }

  deleteSelectedColumns() {
    // Need this in descending order so when we splice it doesn't affect the other indices to remove
    const selectedColumnIndices = this.getAllSelectedColumnIndices().reverse();
    // Remove the cells of source rows
    this.sourceRows.forEach(r => {
      selectedColumnIndices.forEach(idx => {
        r.cells.splice(idx, 1);
      });
    });
    this.columns = this.columns.filter(c => !c.selected);
    this.deselectAll('column');

    // Always have at least one column
    if (this.columns.length === 0) {
      this.addColumnsAtIndex(0);
    }

    this.columns[this.columns.length - 1].selected = true;
    this.statement.columns = this.columns;
    this.calculate();
  }

  categorizeRows(categorizations: Array<Categorization>, shouldRecalculate: boolean = true, supplementalItemCatOverride:boolean=true) {
    categorizations.forEach(c => {
      // Skip headers, supplementalItem (unless override), and ManualInputItem (DAS rows that should never be uncategorized)
      const isProtectedItemClass = c.sourceRow.itemClass === 'supplementalItem' || c.sourceRow.itemClass === 'ManualInputItem';
      // Headers must never be categorized TO a line item, but malformed upstream
      // data (e.g. autonormalization) can leave a header with a stray
      // categorizedTo. Allow the uncategorize path (templateLineItemId === 0) to
      // run on headers so that invalid state can be cleared by the user.
      const isUncategorize = c.templateLineItemId === 0;
      const headerAllowsOperation = !c.sourceRow.header || isUncategorize;
      if(headerAllowsOperation && (!isProtectedItemClass || supplementalItemCatOverride)){
        this.categorizeRow(c);
      }
    });

    if (shouldRecalculate) {
      this.calculate();
    }
    this.setReferenceRows();
  }

  clearCategorization(row: Row) {
    this.categorizeRows([{
      sourceRow: row,
      isValidation: false,
      templateLineItemId: 0,
      templateLineItemLabel: '',
    }],true,false);
  }

  // must make sure there is a selection when using this method
  endingSelectedRow(): Row {
    const selectedRows = this.getAllSelectedRows();
    if (!selectedRows) {
      return;
    }
    const length = selectedRows.length;
    return selectedRows[length - 1];
  }

  isLastRowSelected(): boolean {
    if (!this.sourceRows) {
      return false;
    }
    const length = this.sourceRows.length;
    const finalRow = this.sourceRows[length - 1];
    return finalRow.selected; // boolean isSelected
  }

  isRowSelected(index): boolean {
    if (!this.sourceRows) {
      return false;
    }

    if (this.sourceRows[index]) {
      return !!this.sourceRows[index].selected; // will return true/false even if null/undefined
    }

    return false;
  }

  isColumnSelected(index): boolean {
    if (!this.columns) {
      return false;
    }

    if (this.columns[index]) {
      return !!this.columns[index].selected; // will return true/false even if null/undefined
    }

    return false;
  }

  isFirstRowSelected(): boolean {
    if (!this.sourceRows) {
      return false;
    }
    const firstRow = this.sourceRows[0];
    return firstRow.selected; // boolean isSelected
  }

  endingSelectedRowIndex(): number {
    if (!this.sourceRows) {
      return;
    }
    const length = this.sourceRows.length;
    const lastKey = length - 1;
    for (let i = lastKey; i >= 0; i--) {
      if (this.sourceRows[i].selected) {
        return i;
      }
    }
    // should have returned before this. If not, nothing is selected.
    return 0;
  }

  beginningSelectedRowIndex(): number {
    if (!this.sourceRows) {
      return;
    }
    const length = this.sourceRows.length;
    const lastKey = length - 1;
    for (let i = 0; i <= lastKey; i++) {
      if (this.sourceRows[i].selected) {
        return i;
      }
    }
    // should have returned before this. If not, nothing is selected.
    return 0;
  }

  getFirstNonHeaderRowIndex(): number {
    if (!this.sourceRows) {
      return;
    }

    const length = this.sourceRows.length;
    const lastKey = length - 1;
    for (let i = 0; i <= lastKey; i++) {
      if (!this.sourceRows[i].header) {
        return i;
      }
    }
    // should have returned before this. If not, nothing is selected.
    return 0;
  }

  getLastNonHeaderRowIndex(): number {
    if (!this.sourceRows) {
      return;
    }
    const length = this.sourceRows.length;
    const lastKey = length - 1;
    for (let i = lastKey; i >= 0; i--) {
      if (!this.sourceRows[i].header) {
        return i;
      }
    }
    // should have returned before this. If not, nothing is selected.
    return 0;
  }

  getNumSelectedRows(): number {
    return this.getAllSelectedRows().length;
  }

  areRowsSelected(): boolean {
    return this.getNumSelectedRows() > 0;
  }

  /**
   * Clear the categorizations for all provided rows
   */
  clearAllCategorizations(rows: Array<Row>) {
    this.lastCategorizationPath = [];
    this.categorizeRows(rows.map(r => {
      return {
        sourceRow: r,
        isValidation: false,
        templateLineItemId: 0,
        templateLineItemLabel: '',
        suggestedChange: null
      };
    }),true,false);
    this.setReferenceRows();
  }

  reset() {
    this.resetRows(this.rows);
  }

  /***
   * Recursive function for removing all categorizations from a template
   */
  resetRows(rows: Array<Row>) {
    rows.forEach(r => {
      if (r.template && r.children && r.children.length) {
        if (!r.children[0].template) {
          r.children = [];
        } else {
          this.resetRows(r.children);
        }
      }
    });
  }

  /**
   * Calculate the template rows via rollups and formulas
   */
  calculate() {
    if (this.statementType == DYNAMIC_ADJUSTMENT_STATEMENT){
      this.calculateDASItems();
      this._roundingService.roundRows(this);

      this.reapplyPreviousPeriods();
      return
    }
    this._calculatorRef = this.dasRefSnapshot ? structuredClone(this.dasRefSnapshot) : {};

    // For each normalized cell, set the value to the rollup of its children.
    // Calculate all normalized rollups
    let deferredCalcRows = []
    let orderedRowRefs = structuredClone(Object.keys(this.dasRefSnapshot))
    this.rows.forEach(r => {
      this.calculateRow(r, deferredCalcRows, orderedRowRefs);
    });
    let retry_count = 0
    // If we were unable to resolve refs in any calculated rows, attempt to recalculate those individual rows, up to
    // 4 times. This handles scenarios where referenced items appear below the calculated item in the template. On each
    // subsequent attempt, we should be adding more referencable variables in the scope of the calculation.
    while (retry_count < MAX_RECALC_ATTEMPTS && deferredCalcRows.length){
      retry_count++;
      let rows_to_iter = deferredCalcRows
      deferredCalcRows = []
      rows_to_iter.forEach(r => this.calculateRow(r, deferredCalcRows, orderedRowRefs))
    }
    // If we were still unable to resolve refs for any rows after the max number of attempts, raise an exception. This
    // is most likely to result from a misconfiguration.
    if (deferredCalcRows.length){
      deferredCalcRows.forEach(r => {
        console.error(`Failed to evaluate equation ${r.formula}`)
      })
    }

    this.columns.forEach(c => {
      if (c.columnCheckOk == null) {
        c.columnCheckOk = false;
      }
    });
    this._roundingService.roundRows(this);
    this.determineSubmissionRuleStatusForNonDASStatement();
    // Re-merge previous-period data after every recalculation. Categorize /
    // uncategorize / cell-edit flows all funnel through calculate(); calling
    // reapply here keeps the previous-period view consistent without each
    // call site needing to know about it. Specifically: when a row is
    // uncategorized, this re-runs the splice and a synth prior-only row
    // takes its place under the template parent, preserving the previous-
    // period rollup for that line item even though the current spread no
    // longer has a categorized row contributing to it.
    this.reapplyPreviousPeriods();
  }

  /**
   * Re-run the previous-period merge against the currently stored payloads.
   * Cheap (in-memory tree walk); no network. No-op when no payloads are
   * loaded yet.
   */
  reapplyPreviousPeriods(): void {
    if (!this.previousPeriodPayloads || this.previousPeriodPayloads.size === 0) {
      return;
    }
    this._rebuildPreviousPeriodMerge(this._orderedPreviousPeriodPayloads());
  }

  calculateDASItems(){
    // For DAS statements, we need to dynamically calculate values for rows in the statement, instead of template rows
    this._calculatorRef = {}
    if (Object.keys(this.stdFrameRefSnapshot).length) {
      this._calculatorRef = structuredClone(this.stdFrameRefSnapshot);
    }
    if (this.dasRefSnapshot) {
      this._calculatorRef = {...this._calculatorRef, ...structuredClone(this.dasRefSnapshot)}
    }
    let addBackRefsThatHaveBeenResetInThisCalculation = []
    this.sourceRows.forEach(row => {
      if (row?.ref) {
        if (isDasAddBackRow(row)) {
          if (!addBackRefsThatHaveBeenResetInThisCalculation.includes(row.ref)) {
            delete this._calculatorRef[row.ref];
            addBackRefsThatHaveBeenResetInThisCalculation.push(row.ref)
          }
          // sum the set of add-back items
          if (this._calculatorRef[row.ref]) {
            const nextCalcRefValue = [];
            const newRowValues = row.cells.map(c => this.getAddBackCellValueForCalculations(row, c));
            const existingValues = this._calculatorRef[row.ref];
            for (let i= 0; i<existingValues.length; i++){
              nextCalcRefValue.push(existingValues[i] + newRowValues[i]);
            }
            this._calculatorRef[row.ref] = nextCalcRefValue
          } else {
            this._calculatorRef[row.ref] = row.cells.map(c => this.getAddBackCellValueForCalculations(row, c));
          }
        } else if (isDasCalculatedRow(row) && row.isExcludableInOtherCalculations) {
          if(row.include) {
            this._calculatorRef[row.ref] = row.cells.map(c => row.rollupCoefficient === 0 ? c.value : this.getCellValueForCalculations(row, c));
          } else {
            this._calculatorRef[row.ref] = row.cells.map(c => 0);
          }
        } else if (row.itemClass == 'ManualInputItem') {
          this._calculatorRef[row.ref] = row.cells.map(c => row.rollupCoefficient === 0 ? c.value : this.getCellValueForCalculations(row, c));
        }
      }
    });
    this.statement.rows.forEach(row => {
      if (row?.dynamicAdjustmentStatementItemType==DYNAMIC_ADJUSTMENT_STATEMENT_ITEM_TYPES.CUSTOM_CALCULATION && row.formula) {
        try {
          if (isDasCalculatedRow(row) && row.isExcludableInOtherCalculations && row.include == false) {
            this.evaluateDASRowFormula(row,false);
          } else {
            this.evaluateDASRowFormula(row);
          }
        } catch (e) {
          console.log('failed to eval row formula: ', e)
        }
      }
    });
    this.evaluateSubmissionRulesAndBuildSummary();
  }


  private evaluateSubmissionRulesAndBuildSummary() {
    this.submissionRuleResultsSummary = {} as SubmissionRuleResultsSummary;

    this.sourceRows.forEach(row => {
      const allCellResults = this.evaluateTemplateSubmissionRulesForRow(row);

      if (!allCellResults) {
        this.submissionRuleResultsStatementLevelStatus = 'PASSING';
        return;
      }

      allCellResults.forEach((submissionRuleResults, idx) => {
        if (submissionRuleResults.length > 0) {
          let periodDate = this.columns[idx].statementDate;
          submissionRuleResults.forEach((result) => {
            const resultSummary: SubmissionRuleResultSummary = {
              lineItemLabel: row.label,
              lineItemRef: row.ref,
              action: result.action,
              message: result.message,
              value: row.cells[idx].roundedRawText
            };
            if (!this.submissionRuleResultsSummary[periodDate]) {
              this.submissionRuleResultsSummary[periodDate] = [resultSummary]
            } else {
              this.submissionRuleResultsSummary[periodDate].push(resultSummary)
            }
          });
        }
      });
    });

    this.hasSubmissionRuleResults = Object.keys(this.submissionRuleResultsSummary).length > 0;
    this.determineSubmissionRuleStatusForDASStatement(this.submissionRuleResultsSummary)
  }
  
  determineSubmissionRuleStatusForDASStatement(resultsSummary: SubmissionRuleResultsSummary): void {
    let actions: SubmissionRuleActions[] = [];
    Object.keys(resultsSummary).forEach(periodDate => {
      resultsSummary[periodDate].forEach(result => {
        actions.push(result.action);
      });
    });
    let status: SubmissionRuleActions | 'PASSING' = 'PASSING';
    if (actions.includes(SubmissionRuleActions.HARD_STOP)){
      status = SubmissionRuleActions.HARD_STOP;
    } else if (actions.includes(SubmissionRuleActions.SOFT_STOP)){
      status = SubmissionRuleActions.SOFT_STOP;
    }
    this.submissionRuleResultsStatementLevelStatus = status;
  }
  
  determineSubmissionRuleStatusForNonDASStatement(): void {
    if (!this.templateHasSubmissionRules) {
      return;
    }
    this.submissionRuleResultsStatementLevelStatus = 'PASSING' as SubmissionRuleActions | 'PASSING';
    
    const checkRowAndChildren = (row: Row): void => {
      if (row.cells) {
        for (const cell of row.cells) {
          if (cell.submissionRuleResultsStatus === SubmissionRuleActions.HARD_STOP) {
            this.submissionRuleResultsStatementLevelStatus = SubmissionRuleActions.HARD_STOP;
            return; 
          } else if (cell.submissionRuleResultsStatus === SubmissionRuleActions.SOFT_STOP && 
                     this.submissionRuleResultsStatementLevelStatus !== SubmissionRuleActions.HARD_STOP) {
            this.submissionRuleResultsStatementLevelStatus = SubmissionRuleActions.SOFT_STOP;
          }
        }
      }
      if (row.children) {
        for (const child of row.children) {
          checkRowAndChildren(child);
          if (this.submissionRuleResultsStatementLevelStatus === SubmissionRuleActions.HARD_STOP) {
            return;
          }
        }
      }
    };
    for (const row of this.rows) {
      checkRowAndChildren(row);
      if (this.submissionRuleResultsStatementLevelStatus === SubmissionRuleActions.HARD_STOP) {
        break;
      }
    }
  }

  getAddBackCellValueForCalculations(row, c) {
    if (!row.include) {
      return 0
    }
    let value = row.rollupCoefficient === 0 ? c.value : this.getCellValueForCalculations(row, c)
    if (row.weightPercentage) {
      try {
        const floatWeight = parseFloat(row.weightPercentage);
        const weightAsDecimal = floatWeight/100;
        return value * weightAsDecimal
      } catch (e) {
        console.error(`Failed to calculate raw add-back item. Invalid percentage provided. Item ref: ${row.ref}, weight value: ${row.weightDecimal}`)
        return 0
      }
    }
    return value
  }

  calculateRow(row: Row, deferredCalcRows=[], orderedRowRefs=[]) {
    if (!row.template) {
      return;
    }
    this.initializeRow(row);

    // Calculate all children recursively
    if (row.children) {
      row.children.forEach(child => {
        this.calculateRow(child, deferredCalcRows, orderedRowRefs);
      });

      // Now calculate rollups after we've looped through the children
      this.calculateRollupsForRow(row);
    } else if (row.formula) {
      try {
        this.evaluateRowFormula(row, orderedRowRefs);
      } catch (err) {
        // If we throw this type of error within the evaluation function, it is an issue that may be resolved by a
        // second calculation attempt, after more referenced variables have been resolved.
        if (err instanceof DeferredCalculationError && deferredCalcRows){
          deferredCalcRows.push(row)
          return
        } else {
          throw err
        }
      }
    }

    if (row.ref) {
      // Use 0.0 instead of 'undefined' here so that we can use it in calculations even if we haven't mapped anything.
      // This is a symptom of not using classes w/ defaults but OK for this prototype.
      this._calculatorRef[row.ref] = row.cells.map(c => row.rollupCoefficient === 0 ? c.value : this.getCellValueForCalculations(row, c));
      orderedRowRefs.push(row.ref)
    }

    // Check validation
    if (row.validationRow) {
      this.validateRow(row);
    } else if (row.lineItemId === STANDARD_ITEM_IDS.BALANCE_CHECK) {
      // need special logic to check the "Check" item to confirm balance sheet balances
      this.validateCheckRow(row);
    }

    // Mark overall column if row IS - Net Income checks
    if (this.statementType === INCOME_STATEMENT && row.lineItemId === STANDARD_ITEM_IDS.NET_INCOME ) {
        this.columns.forEach((c, i) => {
          c.columnCheckOk = row.validationRow && !row.cells[i].flag;
        });
    }

    // Mark overall column if row BS - check is OK (BS Check never uses validation row)
    if (this.statementType === BALANCE_SHEET && row.lineItemId === STANDARD_ITEM_IDS.BALANCE_CHECK) {
        this.columns.forEach((c, i) => {
          c.columnCheckOk = !row.cells[i].flag;
        });
    }

    if (row.ref) {
      this.evaluateTemplateSubmissionRulesForRow(row);
    }
  }

  private evaluateTemplateSubmissionRulesForRow(row: Row): SubmissionRuleEvaluationResult[][] | null {
    if (!this.templateHasSubmissionRules || !this.submissionRules[row.ref]) {
      return null;
    }
    const submissionRulesForRow: SubmissionRule[] = this.submissionRules[row.ref];
    const allCellResults: SubmissionRuleEvaluationResult[][] = [];

    row.cells.forEach((cell: Cell, idx) => {
      const submissionRuleResults = [];
      let roundingFactor: ScaleType = "ACTUALS";
      if (!this._roundingService.excludeFromRoundingAndMultiplication(row)){
        roundingFactor =  <ScaleType>this.reviewQueueItem?.multiplyByFactor;
      }
      submissionRulesForRow.forEach(submissionRule => {
        const ruleResult = this._submissionRulesService.evaluateSubmissionRule(submissionRule, cell.value, roundingFactor);
        if (ruleResult) {
          submissionRuleResults.push(ruleResult);
        }
      });
      cell.submissionRuleResults = submissionRuleResults;
      cell.submissionRuleResultsStatus = this.determineCellSubmissionRuleResultsStatus(submissionRuleResults);
      allCellResults.push(submissionRuleResults);
    });

    return allCellResults;
  }
  public determineCellSubmissionRuleResultsStatus(submissionRuleResults: SubmissionRuleEvaluationResult[]): SubmissionRuleActions | null {
    const ruleActions = submissionRuleResults.map(result => result.action);
    if (ruleActions.includes(SubmissionRuleActions.HARD_STOP)){
      return SubmissionRuleActions.HARD_STOP;
    } else if (ruleActions.includes(SubmissionRuleActions.SOFT_STOP)){
      return SubmissionRuleActions.SOFT_STOP;
    }
    return null;
  }

  getAllSelectedRows(): Array<Row> {
    return this.sourceRows.filter(r => r.selected);
  }

  getAllSelectedColumns(): Array<Column> {
    return this.columns.filter(c => c.selected);
  }

  getAllSelectedColumnIndices(): Array<number> {
    return this.columns.map((c, i) => {
      return {
        c: c,
        i: i
      };
    }).filter(c => c.c.selected).map(c => c.i);
  }

  /**
   * Multiply the value of all cells in the selected rows by -1
   * (flip the sign)
   */
  invertSelectedRows() {
    this.invertRows(this.getAllSelectedRows());
  }

  invertRows(rows: Array<Row>) {
    rows.forEach(r => {
      r.invertValue = !r.invertValue;
      // If we suggested a change to the row and a user has inverted it, remove suggestion flag
      if (r.suggestedChange) {
        this.clearGoalSeekSuggestion(r);
      }
    });
    this.calculate();
  }

  getFirstSelectedRow(): Row {
    return this.getFirstSelectedItem('row') as Row;
  }

  getFirstSelectedItem(type: 'row' | 'column'): Row | Column {
    return this.getRowOrColumnEntitiesByType(type).find(r => r.selected);
  }

  getFirstSelectedRowIndex(): number {
    return this.getFirstSelectedItemIndex('row');
  }

  getFirstSelectedItemIndex(type: 'row' | 'column'): number {
    return this.getRowOrColumnEntitiesByType(type).findIndex(r => r.selected);
  }

  rowSelectionAction(action: RowSelectionActionType, itemIdx: number = 0) {
    return this.rowOrColumnSelectionAction('row', action, itemIdx);
  }

  columnSelectionAction(action: RowSelectionActionType, itemIdx: number = 0) {
    return this.rowOrColumnSelectionAction('column', action, itemIdx);
  }

  rowOrColumnSelectionAction(type: 'row' | 'column', action: RowSelectionActionType, itemIdx: number = 0) {
    const sourceEntities = this.getRowOrColumnEntitiesByType(type);

    let lastSelectedIndex, firstSelectedIndex;
    switch (action) {
      // Change selection to the designated row
      case RowSelectionActionType.SELECT_SINGLE:
        this.deselectAll(type);
        if (!sourceEntities[itemIdx]) {
          return;
        }
        sourceEntities[itemIdx].selected = true;
        break;
      // Deselect the designated row
      case RowSelectionActionType.DESELECT_SINGLE:
        if (!sourceEntities[itemIdx]) {
          return;
        }
        sourceEntities[itemIdx].selected = false;
        break;
      case RowSelectionActionType.DESELECT_LAST:
        lastSelectedIndex = this.getLastSelectedItemIndex(type);
        if (!sourceEntities[lastSelectedIndex]) {
          return;
        }
        sourceEntities[lastSelectedIndex].selected = false;
        break;
      case RowSelectionActionType.DESELECT_FIRST:
        firstSelectedIndex = sourceEntities.findIndex(r => r.selected);
        if (!sourceEntities[firstSelectedIndex]) {
          return;
        }
        sourceEntities[firstSelectedIndex].selected = false;

        break;
      // Change selection to the row after the currently selected row
      case RowSelectionActionType.SELECT_NEXT:
        lastSelectedIndex = this.getLastSelectedItemIndex(type);
        if (sourceEntities.length > (lastSelectedIndex + 1)) {
          this.deselectAll(type);
          sourceEntities[lastSelectedIndex + 1].selected = true;
        }
        break;
      // Change selection to the row before the first selected row
      case RowSelectionActionType.SELECT_PREVIOUS:
        firstSelectedIndex = sourceEntities.findIndex(r => r.selected);

        if (firstSelectedIndex > 0) {
          this.deselectAll(type);
          sourceEntities[firstSelectedIndex - 1].selected = true;
        }
        break;
      // Add the row immediately before the first selected row to the selection
      case RowSelectionActionType.INCLUDE_PREVIOUS:
        firstSelectedIndex = sourceEntities.findIndex(r => r.selected);
        if (firstSelectedIndex > 0) {
          sourceEntities[firstSelectedIndex - 1].selected = true;
        }
        this.shiftDirection = 'up';
        break;
      // Add the designated row to the selection
      case RowSelectionActionType.INCLUDE_ADDITIONAL:
        if (!sourceEntities[itemIdx]) {
          return;
        }
        sourceEntities[itemIdx].selected = true;
        break;
      // Add the row immediately after the last selected row to the selection
      case RowSelectionActionType.INCLUDE_NEXT:
        lastSelectedIndex = this.getLastSelectedItemIndex(type);
        if (sourceEntities.length > (lastSelectedIndex + 1)) {
          sourceEntities[lastSelectedIndex + 1].selected = true;
        }
        this.shiftDirection = 'down';
        break;
      // Add all rows between the current selection and the designated row to the selection
      case RowSelectionActionType.INCLUDE_UNTIL:
        lastSelectedIndex = this.getLastSelectedItemIndex(type);
        firstSelectedIndex = sourceEntities.findIndex(r => r.selected);
        if (itemIdx > lastSelectedIndex) {
          for (let i = lastSelectedIndex + 1; i <= itemIdx; i++) {
            sourceEntities[i].selected = true;
          }
        } else if (itemIdx < firstSelectedIndex) {
          for (let i = itemIdx; i < firstSelectedIndex; i++) {
            sourceEntities[i].selected = true;
          }
        }
        break;
      case RowSelectionActionType.DESELECT_ALL:
        this.deselectAll(type);
        break;
    }

    if ( (action !== RowSelectionActionType.INCLUDE_PREVIOUS &&
      action !== RowSelectionActionType.INCLUDE_NEXT) && this.getNumSelectedRows() <= 1) {
        this.shiftDirection = 'neutral';
      }

    this.lastSelectionAction = action;
  }

  /**
   * Flatten the rows so we can render the table in a real table rather
   * than a list.
   */
  getPreviewRows(): Array<Row> {
    return [].concat.apply([], this.rows.map(r => this.flattenRow(r)));
  }


  /**
   * Allows us to invert by flipping the rollup coefficient and uses that
   * to return the value for a cell that we should be using for any calc
   */
  getCellValueForCalculations(row: Row, cell: Cell): number {
    if (cell && cell.value) {
      // @ts-ignore
      const value = parseFloat(cell.value);
      return row.invertValue ?  value * -1 : value;
    } else {
      return 0.0;
    }
  }

  deselectAll(type = 'row') {
    this.getRowOrColumnEntitiesByType(type).forEach(e => {
      e.selected = false;
    });
  }

  getLastSelectedItemIndex(type: 'row' | 'column'): number {
    const filtered = this.getRowOrColumnEntitiesByType(type).map((r, i) => {
      return {
        r: r,
        i: i
      };
    }).filter(r => r.r.selected)

    if (!filtered.length) {
      return -1;
    }

    return filtered.slice(-1)[0].i;
  }

  enableGoalSeekForIncomeStatement() {
    return this.enoughSourceRowsCategorizedForGoalSeek() && !this.netIncomeValidates()
  }

  enoughSourceRowsCategorizedForGoalSeek() {
    const nonHeaderRows = this.sourceRows.filter(sr => !sr.header);
    if (nonHeaderRows.length === 0) {
      return false;
    }
    const categorizedRows = nonHeaderRows.filter(sr => sr.categorizedTo > 0);
    return (categorizedRows.length / nonHeaderRows.length) > this.goalSeekRowCategorizationPercent;
  }

  netIncomeValidates() {
    let valid = false;
    this.rows.forEach(row => {
      // id 62400 is Net Income and is not expected to change
      if (row.lineItemId === 62400 && row.validationRow) {
        valid = row.cells.every(function(c) {
          return !c.flag;
        })
      }
    })
    return valid;
  }

  clearGoalSeekSuggestion(row: Row) {
    this.goalSeekCount--;
    row.suggestedChange = null;
  }
  
  clearAllGoalSeekSuggestions() {
    this.sourceRows.forEach(r => {
      r.suggestedChange = null;
      r.suggestionOrigin = null;
    });
    this.goalSeekCount = 0;
  }

  updateFromGoalSeekSuggestions(suggestions: Array<Suggestion>, originLineItemId: number) {
    this.goalSeekCount = 0;
    this.sourceRows.forEach(r => {
      suggestions.forEach(s => {
        if (r.lineItemId === s.lineItemId) {
          r.suggestionOrigin = originLineItemId;
          this.goalSeekCount++;
          switch (s.operation) {
            case 'invert':
              r.suggestedChange = 'Try inverting this line item';
              break;
            case 'uncategorize':
              r.suggestedChange = 'Try uncategorizing this line item'
              break;
            case 'categorize':
              r.suggestedChange = 'Try categorizing this line item'
              break;
            case 'categorize_invert':
              r.suggestedChange = 'Try categorizing this line item'
              break;
          }
        }
      })
    })
    this.calculate();
  }

  getGoalSeekSourceRows(row: Row, columnIdx: number) {
    // get a list of source rows eligible to be changed
    const sourceRows = this.sourceRows.filter(sr => (sr.categorizationType !== 'validation') && (!sr.header));

    // from this list, filter out anything that is already part of a correctly validated item
    this.rows.forEach(r => {
      if (r.cells[columnIdx].offBy === 0) {
        // recursively remove sourceRows that are part of validated items
        this.removeValidatedRows(sourceRows, r);
      }
    });
    this.populateGoalSeekRollupCoefficients(sourceRows, row.lineItemId);
    return sourceRows;
  }

  populateGoalSeekRollupCoefficients(rows: Array<Row>, goalSeekLineItemId: number) {
    rows.forEach(r => {
      if (r.categorizedTo === 0) {
        // if something hasnt been categorized yet, it doesnt matter what we assume here, as we will
        // check both positive and negative rollups, and the user will need to confirm
        r.goalSeekCoefficient = 1;
      } else if (this.goalSeekRollupMap.has(goalSeekLineItemId) && this.goalSeekRollupMap.get(goalSeekLineItemId).has(r.categorizedTo)) {
        r.goalSeekCoefficient = this.goalSeekRollupMap.get(goalSeekLineItemId).get(r.categorizedTo);
      } else {
        // this line items categorization is not relevant to the goal seek row
        r.goalSeekCoefficient = 0;
      }
    })
  }

  populateGoalSeekRollupMap() {
    this.goalSeekRollupMap = new Map<number, Map<number, number>>();
    // iterate over highest level template rows and populate rollup maps
    this.rows.forEach(async row => {
      const rollupMap = new Map<number, number>();
      this.getRollupMapForRow(row, 1, rollupMap);
      this.goalSeekRollupMap.set(row.lineItemId, rollupMap);
    })
  }

  /**
   * Cleans a formula to by transforming it into REF components and +,- operators
   * separated by spaces
   * @param formula
   */
  cleanFormula(formula: string): string {
    formula = formula.replace('=', '');
    formula = formula.replace('+', ' + ').replace('-', ' - ');
    // replace double (or more) spaces with single
    formula = formula.replace(/\s{2,}/g, ' ').trim();
    return formula;
  }

  private getRollupMapForRow(row: Row, rollupCoefficient: number, rollupMap: Map<number, number>) {
    rollupMap.set(row.lineItemId, rollupCoefficient);
    if (row.hasOwnProperty('children')) {
      this.getRollupMapFromChildren(row, rollupCoefficient, rollupMap);
    } else if (row.hasOwnProperty('formula')) {
      this.getRollupMapFromFormula(row, rollupCoefficient, rollupMap);
    }
  }

  private getRollupMapFromChildren(row: Row, rollupCoefficient: number, rollupMap: Map<number, number>) {
    row.children.forEach(child => {
      // make sure the child rows are not source row items that have been categorized
      if (child.template) {
        rollupMap.set(child.lineItemId, child.rollupCoefficient * rollupCoefficient);
        this.getRollupMapForRow(child, child.rollupCoefficient * rollupCoefficient, rollupMap)
      }
    });
  }

  private getRollupMapFromFormula(row: Row, rollupCoefficient: number, rollupMap: Map<number, number>) {
    let formulaCoefficient = rollupCoefficient;
    const cleanFormula = this.cleanFormula(row.formula)
    cleanFormula.split(' ').forEach(component => {
      // if we come across addition / substraction, note it for the next item
      if (component === '+') {
        formulaCoefficient = rollupCoefficient;
      } else if (component === '-') {
        formulaCoefficient = -1 * rollupCoefficient;
      } else {
        this.rows.forEach(r => {
          if (r.ref === component) {
            this.getRollupMapForRow(r, formulaCoefficient, rollupMap);
          }
        })
      }
    })
  }

  /**
   * Given a list of statement source rows and a validated total row, recursively
   * remove all child rows of validated row
   *
   * @param rows
   * @param validatedRow
   */
  private removeValidatedRows(sourceRows: Array<Row>, validatedRow: Row) {
    if (validatedRow.children) {
      validatedRow.children.forEach(child => {
        this.removeValidatedRows(sourceRows, child);
        const sourceRowIndex = sourceRows.indexOf(child);
        if (sourceRowIndex !== -1) {
          sourceRows.splice(sourceRowIndex, 1);
        }
      });
    }
  }

  private getRowOrColumnEntitiesByType(type = 'row'): Array<Row | Column> {
    if (type === 'row') {
      return this.sourceRows;
    }
    return this.columns;
  }

  private flattenRow(row: Row, level = 0): Array<Row> {
    row.previewIndentation = level;
    let rv = [row];

    if (row.children) {
      rv = rv.concat.apply(rv, row.children.map(c => this.flattenRow(c, level + 1)));
    }

    if (row.rollupName) {
      rv.push({
        'label': row.rollupName,
        'cells': row.cells,
        'lineItemId': row.lineItemId,
        'invertValue': row.invertValue,
        'calculated': true,
        'previewIndentation': level,
      });
    }
    return rv;
  }

  /**
   * Validates the "Check" row on balance sheet
   * This row should always have values equal to zero, but never has a
   * "validation" row assigned to it
   */
  private validateCheckRow(row: Row) {
    row.cells.forEach(cell => {
      if (Math.abs(cell.value) > 1.0) {
        cell.flag = true;
        cell.flagReason = `Off by ${cell.value.toFixed(2)}`;
        cell.offBy = -cell.value;
      } else {
        cell.flag = false;
        cell.flagReason = '';
        cell.offBy = 0.0;
      }
    });
  }

  /**
   * Validates a row that is expected to match 1:1 with a designated
   * other row. Sets the cell flag + offBy values for each cell that is
   * different from the expected value.
   *
   * @param row
   */
  private validateRow(row: Row) {
    // Skip active-cell validation when the validationRow is a prior-only
    // synth (created when the user uncategorized the current spread's
    // validation row but a prior period had one). A synth has cells: [],
    // so without this guard every active cell would be flagged against an
    // expected value of 0. Prev-period validation runs separately via
    // _validatePreviousPeriodRow against the synth's previousCells.
    if (row.validationRow && row.validationRow.isPreviousOnlyRow) {
      return;
    }
    row.cells.forEach((cell, idx) => {
      const expectedValue = this.getCellValueForCalculations(row.validationRow, row.validationRow.cells[idx])

      if (!cell.value) {
        cell.value = 0.0;
      }
      const cv = row.rollupCoefficient === 0 ? cell.value : this.getCellValueForCalculations(row, cell);

      if (typeof expectedValue !== 'number' || typeof cv !== 'number' ) {
        cell.flag = true; // flag since something is wrong, but don't know what
        cell.flagReason = 'Validation or value is not a number';
        cell.offBy = 0.0;
      } else {
        if (expectedValue.toFixed(2) !== cv.toFixed(2)) {
          const offBy = cv - expectedValue;
          cell.flag = true;
          cell.flagReason = `Off by ${offBy.toFixed(2)}`;
          cell.offBy = offBy;
        } else {
          cell.flag = false;
          cell.flagReason = '';
          cell.offBy = 0.0;
        }
      }
    })
  }

  /**
   * Initializes a row with the correct number of cells based on the
   * current column count
   * @param row
   */
  private initializeRow(row: Row) {
    row.cells = new Array<Cell>();
    this.columns.forEach(_ => {
      const cell: Cell = {
        value: null
      };
      row.cells.push(cell);
    });
  }

  extractVariablesFromEquation(formula: string) {
    /**
     * Use the builtin mathjs parse/filter methods to extract all variable references from the formula we are about to
     * evaluate.
     */
    const node = math.parse(formula);
    return node.filter((node) => {
      return node.isSymbolNode
    }).map(symbolNode => symbolNode.name)
  }


  checkForOutOfOrderRefs(equationRefs: Array<string>, orderedRowRefs: Array<string>): void {
    /**
     * We are using the 'orderedRowRefs' array to track the line items that have already been calculated in this
     * iteration, and are therefore referencable in the current equation. If we locate a ref that is not yet tracked in
     * this array, we cannot reliably inject it into the equation, even if a corresponding value exists in scope. We
     * expect this to occur when an equation referenced an item that appears below it in the template. To handle this
     * scenario, we raise a 'DeferredCalc...' exception, which is caught by the caller and triggers another attempted
     * calculation, once all other valid rows have been calculated and added to the refs array.
     */
    if (equationRefs && orderedRowRefs) {
      equationRefs.forEach(ref => {
        if (!orderedRowRefs.includes(ref)) {
          throw new DeferredCalculationError(`Out Of Order Ref: ${ref}`)
        }
      })
    }
  }

  private evaluateDASRowFormula(row: Row, included=true) {

    row.cells.forEach((c, idx) => {
      let value = 0;
      const equation = (row.formula.startsWith('=')) ? row.formula.substring(1) : row.formula;
      const scope = this.equationScopeForColumn(idx);
      if (!equation.toLowerCase().includes('PRIOR('.toLowerCase())) { // if PRIOR not in equation
        try {
          value = math.evaluate(equation, scope);
        } catch (e) {
          console.error(`Failed to eval equation: `, equation, scope)
        }
      }
      if ([Infinity, -Infinity, NaN].includes(value)) {
        value = 0;
      }
      c.rawText = `${value}`
      c.itemRowValue = value;
      c.value = value;

      const roundedValue = this._roundingService.roundCellValue(this, row, value);
      c.roundedRawText = `${roundedValue}`

      if(included){
      // If row is a calculated item, update the reference in _calculatorRef
        if (isDasCalculatedRow(row)) {
          try {
            this._calculatorRef[row.ref][idx] = value;
          } catch (e) {
            if (print) {
              console.warn(`Failed to update _calculatorRef for ref ${row.ref} at index ${idx}`, e);
            }
          }
        }
      }
    });
  }

  /**
   * Evaluates a row's formula given other calculated values
   * @param row
   * @param orderedRowRefs
   */
  private evaluateRowFormula(row: Row, orderedRowRefs = []) {
    const equation = (row.formula.startsWith('=')) ? row.formula.substring(1) : row.formula;
    const equationRefs = this.extractVariablesFromEquation(equation);
    this.checkForOutOfOrderRefs(equationRefs, orderedRowRefs)

    row.cells.forEach((c, idx) => {
      let value = 0;
      const scope = this.equationScopeForColumn(idx);
      if (!equation.toLowerCase().includes('PRIOR('.toLowerCase())) { // if PRIOR not in equation
        try {
          value = math.evaluate(equation, scope);
        } catch (e) {
          console.log(`Failed to eval equation. Calculation will be reattempted. Current equation and available refs: `, equation, scope)
          throw new DeferredCalculationError(`failed to eval equation: ${equation} ${scope}`)

        }
      }
      c.itemRowValue = value;
      c.value = value;
    });
  }

  private calculateRollupsForRow(row: Row) {
    // If "validation only", don't roll this up to calculate the actual cell value
    const rollupCoefficients = row.children.map(c => c.rollupCoefficient);
    row.cells.forEach((cell, idx) => {
      const sourceCells = [];
      const childCells = row.children.map(c => {
        const cs = Object.assign({}, c.cells[idx]);
        if (c.invertValue === true) {
          cs.value = (cs.value ? cs.value : 0.0) * -1;
        }
        if (!c.template) {
          sourceCells.push(cs);
        }
        return cs;
      });
      // const
      cell.itemRowValue = this.rollupSum(rollupCoefficients, sourceCells);
      cell.value = this.rollupSum(rollupCoefficients, childCells);
    });
  }

  private equationScopeForColumn(
    idx: number,
    calcRef: Record<string, Array<number>> = this._calculatorRef,
  ): Record<string, number> {
    const scope: Record<string, number> = {};
    Object.keys(calcRef).forEach(k => {
      scope[k] = calcRef[k][idx];
    });

    return scope;
  }

  private rollupSum(rowRollupCoefficients: Array<number>, cells: Array<Cell>): number {
    if (cells.length === 0) {
      return 0.0;
    }
    return cells.map((cell, idx) => ((cell && cell.value) ? cell.value : 0.0) * rowRollupCoefficients[idx]).reduce((a, b) => a + b);
  }

  private categorizeRow(categorization: Categorization) {
    if (categorization.templateLineItemId === undefined) {
      return;
    }

    // Save the original categorizedTo value before overwriting it (needed for uncategorizing)
    const originalCategorizedTo = categorization.sourceRow.categorizedTo;

    // Set the properties on the row so we can display its categorization information correctly
    categorization.sourceRow.categorizedTo = categorization.templateLineItemId;
    if (categorization.isValidation) {
      categorization.sourceRow.categorizationType = 'validation';
      // if unlabeled - take label from validation item
      if (categorization.sourceRow.label === UNLABELED_ROW_PREFIX) {
        categorization.sourceRow.label = categorization.templateLineItemLabel;
      }
      // go remove any other rows validated to the same item
      this.sourceRows.forEach(sourceRow => {
        if (categorization.templateLineItemId === sourceRow.categorizedTo && sourceRow.categorizationType === 'validation' && sourceRow.uuid !== categorization.sourceRow.uuid) {
          this.clearCategorization(sourceRow);
        }
      })
    } else {
      if (categorization.templateLineItemId > 0) {
        categorization.sourceRow.categorizationType = 'component';
      } else {
        categorization.sourceRow.categorizationType = null;
      }
    }

    categorization.sourceRow.categorizationLabel = categorization.templateLineItemLabel;

    // If we had suggested changes to this row and the user updated it, remove suggestion flag
    if (categorization.sourceRow.suggestedChange) {
      this.clearGoalSeekSuggestion(categorization.sourceRow);
    }

    // Row has been (re) categorized - remove all flags
    categorization.sourceRow.cells.forEach(c => {
      c.flag = false;
      c.flagReason = '';
    });

    this.rows.forEach(templateRow => {
      searchAndDestroy(templateRow, categorization.sourceRow, categorization.templateLineItemId === 0);
    });

    // Now add it to the right spot
    // When uncategorizing (templateLineItemId === 0), pass the original categorizedTo value
    const originalNormalizedItemId = (categorization.templateLineItemId === 0 && originalCategorizedTo > 0) ? originalCategorizedTo : undefined;

    this.rows.some(templateRow => {
      const res = searchAndAdd(templateRow, categorization.templateLineItemId, categorization.isValidation, categorization.sourceRow, [templateRow], originalNormalizedItemId);
      if (res[0]) {
        this.lastCategorizationPath = res[1];
        return true;
      }
      return false;
    });
  }

  getColumnDatesSummary(): string[] {
    return this.columns.map(column => column.statementDate || '');
  }

  /**
   * Rebuilds broken object references after JSON deserialization.
   * JSON serialization breaks references between rows and sourceRows
   * So we need to rebuild the missing reference of validationRow
   */
  rebuildRowReferences(): void {
    if (!this.sourceRows || !this.rows) {
      return;
    }

    // Create a map of sourceRows by uuid for fast lookup
    const sourceRowMap = new Map<string, Row>();
    this.sourceRows.forEach((row: Row) => {
      if (row.uuid) {
        sourceRowMap.set(row.uuid, row);
      }
    });

    // Recursively rebuild broken references in template rows
    const rebuildReferences = (templateRow: Row) => {
      if (templateRow.validationRow?.uuid) {
        templateRow.validationRow = sourceRowMap.get(templateRow.validationRow.uuid) || null;
      }

      // Rebuild children references (can be sourceRows or nested template rows)
      if (templateRow.children?.length) {
        templateRow.children = templateRow.children
          .map((child: Row) => {
            // If child is a sourceRow, find matching one by UUID
            if (child.uuid && !child.template) {
              return sourceRowMap.get(child.uuid) || null;
            }
            // If child is a template row, rebuild its references recursively
            if (child.template) {
              rebuildReferences(child);
              return child;
            }
            return child;
          })
          .filter((child: Row) => child !== null);
      }
    };

    // Rebuild references for all template rows
    this.rows.forEach((row: Row) => rebuildReferences(row));

    // Rebuild references for referenceRows
    this.setReferenceRows();
  }

  // -- Previous periods in data categorization --

  /**
   * Store the metadata response from `GET /previous-periods`. Does NOT
   * fetch payloads — the caller is expected to follow up with
   * applyPreviousPeriodSelection() once payloads have been resolved.
   */
  setAvailablePreviousPeriods(opts: {
    availablePeriods: Array<PreviousPeriodMeta>;
    defaultPeriodKey: string | null;
    selectedPeriodKeys: Array<string>;
  }): void {
    this.availablePreviousPeriods = opts.availablePeriods || [];
    this.defaultPreviousPeriodKey = opts.defaultPeriodKey || null;
    const validKeys = new Set(this.availablePreviousPeriods.map(p => p.periodKey));
    this.selectedPreviousPeriodKeys = (opts.selectedPeriodKeys || []).filter(k => validKeys.has(k));
  }

  /**
   * Apply a list of resolved previous-period payloads. Rebuilds:
   *   - previousPeriodColumns (sorted oldest -> newest)
   *   - row.previousCells on every template row in state.rows
   *   - synthetic prior-only source rows under each template line item
   *
   * Caller passes only the payloads matching selectedPreviousPeriodKeys.
   * Payloads not in selectedPreviousPeriodKeys are dropped.
   */
  applyPreviousPeriodPayloads(payloads: Array<PreviousPeriodPayload>): void {
    const selectedSet = new Set(this.selectedPreviousPeriodKeys);
    const filtered = (payloads || []).filter(p => selectedSet.has(p.periodKey));

    this.previousPeriodPayloads = new Map(filtered.map(p => [p.periodKey, p] as [string, PreviousPeriodPayload]));

    this._rebuildPreviousPeriodMerge(this._orderedPreviousPeriodPayloads());
  }

  /**
   * Read-only sorted view (oldest -> newest by statementDate) of the
   * payloads currently stored in `previousPeriodPayloads`. Used by both
   * the initial apply and re-apply paths so they share an ordering.
   */
  private _orderedPreviousPeriodPayloads(): Array<PreviousPeriodPayload> {
    return Array.from(this.previousPeriodPayloads.values()).sort((a, b) => {
      const da = a.column?.statementDate || '';
      const db = b.column?.statementDate || '';
      return da < db ? -1 : da > db ? 1 : 0;
    });
  }

  /**
   * Pure merge step for the prev-period feature. Assumes
   * `previousPeriodPayloads` already holds the canonical (filtered)
   * payload set; rebuilds:
   *   - previousPeriodColumns (sorted oldest -> newest)
   *   - row.previousCells on every template row in state.rows
   *   - synthetic prior-only source rows under each template line item
   * Both `applyPreviousPeriodPayloads` (initial set) and
   * `reapplyPreviousPeriods` (calc()-driven reapply) call this directly
   * so re-applies don't re-store the payload map.
   */
  private _rebuildPreviousPeriodMerge(orderedPayloads: Array<PreviousPeriodPayload>): void {
    // Backend serializes prior-period statement dates as ISO (YYYY-MM-DD);
    // the current spread's columns arrive pre-formatted as MM/DD/YYYY.
    // Normalize here so the template renders both the same way. Pure string
    // rewrite avoids the timezone shift that Date parsing of date-only ISO
    // strings would introduce in negative-UTC locales.
    this.previousPeriodColumns = orderedPayloads.map(p => {
      const sd = p.column?.statementDate;
      const parts = (sd || '').split('-');
      const formatted = parts.length === 3 ? `${parts[1]}/${parts[2]}/${parts[0]}` : sd;
      return {
        ...p.column,
        statementDate: formatted,
        isPreviousPeriod: true,
        previousPeriodKey: p.periodKey,
      };
    });

    const numPrevCols = this.previousPeriodColumns.length;

    if (this.statementType == DYNAMIC_ADJUSTMENT_STATEMENT) {
      this._rebuildPreviousPeriodMergeForDAS(orderedPayloads);
      return;
    }

    // Reset prior synthetic rows + previousCells from a previous render pass.
    this._stripPreviousOnlyRows();
    this._initializePreviousCells(this.rows, numPrevCols);
    this._initializePreviousCells(this.sourceRows, numPrevCols);

    // Index template rows by lineItemId once and share across the splice
    // and seed steps; they previously each rebuilt the same map.
    const templateRowsByLineItemId = this._indexTemplateRowsByLineItemId();

    // 1. Splice synthetic prior-only source rows under each matching template
    //    parent (so subsequent rollups can include their values).
    // 2. Populate every source row's previousCells (categorized rows are the
    //    same object as their template-tree child; mutation propagates).
    // 3. Seed template-row previousCells from lineItemValues for any row whose
    //    lineItemId is present in the STANDARD DataFrame's _items (handles
    //    formula/calculated leaves that don't roll up from children).
    // 4. Rollup pass — mirrors calculateRollupsForRow so parent template rows
    //    are summed from their children's previousCells with rollupCoefficient
    //    and invertValue applied. This is the canonical pattern used for
    //    active cells; matching it here keeps the two flows symmetrical.
    this._spliceInPriorOnlySourceRows(orderedPayloads, templateRowsByLineItemId);
    this._populatePreviousCellsOnSourceRows(orderedPayloads);
    this._seedTemplatePreviousCellsFromLineItemValues(orderedPayloads, templateRowsByLineItemId);
    this._calculatePreviousPeriodTree();
  }

  /**
   * DAS variant of the previous-period merge. Prior source rows align to
   * current rows by label and order of appearance, so repeated labels stay
   * distinct rows rather than collapsing into one.
   * The rows that cannot match — chiefly add-back rows whose raw item was
   * categorized differently in the prior spread — become synthetic
   * read-only rows in dasPreviousOnlyRowsByAnchor, keyed by the uuid of
   * the current row they should render after. sourceRows itself stays
   * untouched so saves, calculateDASItems(), and submission rules never
   * see synths.
   */
  private _rebuildPreviousPeriodMergeForDAS(orderedPayloads: Array<PreviousPeriodPayload>): void {
    this.dasPreviousOnlyRowsByAnchor = {};
    const currentRowsByKey = new Map<string, Array<Row>>();
    for (const sr of this.sourceRows || []) {
      sr.previousCells = orderedPayloads.map(() => ({ value: undefined } as Cell));
      const key = this._dasRowKey(sr);
      if (!key) {
        continue;
      }
      if (!currentRowsByKey.has(key)) {
        currentRowsByKey.set(key, []);
      }
      currentRowsByKey.get(key).push(sr);
    }

    const synthsByKey = new Map<string, Row>();
    orderedPayloads.forEach((payload, payloadIdx) => {
      const claimed = new Set<Row>();
      const priorCounts = new Map<string, number>();
      let anchorUuid = this.sourceRows?.[0]?.uuid ?? '';
      for (const priorRow of payload.sourceRows || []) {
        const key = this._dasRowKey(priorRow);
        if (!key) {
          continue;
        }
        const occurrenceKey = nextOccurrenceKey(priorCounts, key);
        const currentRow = (currentRowsByKey.get(key) || []).find(r => !claimed.has(r));
        if (currentRow) {
          claimed.add(currentRow);
          anchorUuid = currentRow.uuid ?? anchorUuid;
          if (!priorRow.header) {
            currentRow.previousCells[payloadIdx] = this._dasPreviousCell(priorRow);
          }
          continue;
        }
        if (priorRow.header) {
          continue;
        }
        let synth = synthsByKey.get(occurrenceKey);
        if (!synth) {
          synth = this._newDasPreviousOnlyRow(occurrenceKey, priorRow, orderedPayloads.length);
          synthsByKey.set(occurrenceKey, synth);
          if (!this.dasPreviousOnlyRowsByAnchor[anchorUuid]) {
            this.dasPreviousOnlyRowsByAnchor[anchorUuid] = [];
          }
          this.dasPreviousOnlyRowsByAnchor[anchorUuid].push(synth);
        }
        synth.previousCells[payloadIdx] = this._dasPreviousCell(priorRow);
      }
    });

    this._calculatePreviousPeriodDASItems(orderedPayloads);
  }

  /**
   * Mirrors calculateDASItems for previousCells. DAS custom-calculation rows
   * are persisted with zeroed cells whenever a non-DAS tab is saved, so their
   * prior values have to be recalculated here for the same reason the live
   * view recalculates them rather than reading the stored cells.
   */
  private _calculatePreviousPeriodDASItems(orderedPayloads: Array<PreviousPeriodPayload>): void {
    if (!orderedPayloads.length) return;

    const prevCalcRef: Record<string, Array<number>> = {};
    orderedPayloads.forEach((payload, idx) => {
      for (const refValue of payload.refValues || []) {
        if (!prevCalcRef[refValue.ref]) {
          prevCalcRef[refValue.ref] = orderedPayloads.map(() => 0);
        }
        prevCalcRef[refValue.ref][idx] = refValue.value;
      }
    });

    const synths = Object.values(this.dasPreviousOnlyRowsByAnchor || {}).flat();
    const rows = [...(this.sourceRows || []), ...synths];

    const resetAddBackRefs = new Set<string>();
    rows.forEach(row => {
      if (!row?.ref || !row.previousCells) return;
      if (isDasAddBackRow(row)) {
        if (!resetAddBackRefs.has(row.ref)) {
          delete prevCalcRef[row.ref];
          resetAddBackRefs.add(row.ref);
        }
        const values = row.previousCells.map(c => this._previousDasAddBackCellValue(row, c));
        const existing = prevCalcRef[row.ref];
        prevCalcRef[row.ref] = existing ? existing.map((v, i) => v + values[i]) : values;
      } else if (isDasCalculatedRow(row) && row.isExcludableInOtherCalculations) {
        prevCalcRef[row.ref] = row.previousCells.map(c =>
          c?.previousDasInclude === false ? 0 : this._previousDasCellValue(row, c)
        );
      } else if (row.itemClass === 'ManualInputItem') {
        prevCalcRef[row.ref] = row.previousCells.map(c => this._previousDasCellValue(row, c));
      }
    });

    rows.forEach(row => {
      if (row?.dynamicAdjustmentStatementItemType !== DYNAMIC_ADJUSTMENT_STATEMENT_ITEM_TYPES.CUSTOM_CALCULATION) return;
      if (!row.formula || !row.previousCells) return;
      this._evaluatePreviousPeriodDASRowFormula(row, prevCalcRef);
    });
  }

  private _previousDasCellValue(row: Row, cell: Cell): number {
    if (row.rollupCoefficient === 0) {
      return cell?.value ?? 0;
    }
    return this.getCellValueForCalculations(row, cell);
  }

  /**
   * Mirrors getAddBackCellValueForCalculations, but the include flag and weight
   * come from the prior spread's selections on the cell rather than the current
   * row. Note the prior weight is already a decimal; row.weightPercentage isn't.
   */
  private _previousDasAddBackCellValue(row: Row, cell: Cell): number {
    if (cell?.previousDasInclude === false) {
      return 0;
    }
    const value = this._previousDasCellValue(row, cell);
    const weight = cell?.previousDasWeightDecimal;
    return weight == null ? value : value * weight;
  }

  private _evaluatePreviousPeriodDASRowFormula(row: Row, prevCalcRef: Record<string, Array<number>>): void {
    const equation = row.formula.startsWith('=') ? row.formula.substring(1) : row.formula;

    row.previousCells.forEach((cell, idx) => {
      let value = 0;
      if (!equation.toLowerCase().includes('prior(')) {
        try {
          value = math.evaluate(equation, this.equationScopeForColumn(idx, prevCalcRef));
        } catch (e) {
          value = 0;
        }
      }
      if ([Infinity, -Infinity, NaN].includes(value)) {
        value = 0;
      }
      cell.value = value;

      const excluded = row.isExcludableInOtherCalculations && cell?.previousDasInclude === false;
      if (!excluded && row.ref && isDasCalculatedRow(row)) {
        if (!prevCalcRef[row.ref]) {
          prevCalcRef[row.ref] = row.previousCells.map(() => 0);
        }
        prevCalcRef[row.ref][idx] = value;
      }
    });
  }


  private _dasRowKey(row: { label?: string; userFacingLabel?: string; header?: boolean }): string | null {
    const norm = (row.userFacingLabel || row.label || '').trim().toLowerCase();
    if (!norm) {
      return null;
    }
    return `${row.header ? 'h' : 'r'}:${norm}`;
  }

  private _dasPreviousCell(priorRow: PreviousPeriodSourceRow): Cell {
    return {
      value: priorRow.value == null ? undefined : priorRow.value,
      sourceComments: priorRow.sourceComments,
      previousDasWeightDecimal: priorRow.weightDecimal,
      previousDasInclude: priorRow.include,
    };
  }

  private _newDasPreviousOnlyRow(
    key: string,
    priorRow: PreviousPeriodSourceRow,
    numPrevCols: number,
  ): Row {
    return {
      uuid: `__prior__:${key}`,
      ref: priorRow.ref,
      label: priorRow.label || '',
      isPreviousOnlyRow: true,
      labelIsLocked: true,
      header: false,
      // Empty active cells keep the row's <td> count aligned with the
      // current columns; the DAS table renders them as locked "-" cells.
      cells: (this.columns || []).map(() => ({ value: undefined } as Cell)),
      previousCells: Array.from({ length: numPrevCols }, () => ({ value: undefined } as Cell)),
      indentation: priorRow.indentation ?? 0,
      itemClass: priorRow.itemClass,
      invertValue: priorRow.invertValue,
      textFormat: priorRow.textFormat,
      dynamicAdjustmentStatementItemType: priorRow.dynamicAdjustmentStatementItemType,
    };
  }

  private _indexTemplateRowsByLineItemId(): Map<number, Row> {
    const m = new Map<number, Row>();
    const visit = (row: Row) => {
      if (row.lineItemId != null) m.set(row.lineItemId, row);
      if (row.children) for (const c of row.children) visit(c);
    };
    if (this.rows) for (const r of this.rows) visit(r);
    return m;
  }

  // ---- internal helpers ----

  private _stripPreviousOnlyRows(): void {
    const strip = (rows: Array<Row>) => {
      if (!rows) return rows;
      for (const row of rows) {
        // Clear validation-row slots that were filled by a synth in a
        // previous merge pass. Real (current-spread) validation rows are
        // never marked isPreviousOnlyRow, so they're left alone.
        if (row.validationRow && row.validationRow.isPreviousOnlyRow) {
          row.validationRow = null;
        }
        // Drop the prior-only slot wholesale. Synths live exclusively
        // here, so we never have to filter them out of `children`.
        row.previousOnlyChildren = undefined;
        if (row.children && row.children.length) {
          strip(row.children);
        }
      }
      return rows;
    };
    strip(this.rows);
  }

  /**
   * Initialize previousCells on every row in `rows` (recursively) to
   * an array of `{value: undefined}` of length numCols. Mirrors how
   * initializeRow() sets up `cells` for active columns. Idempotent: a
   * matching-length array on a row is left in place so we don't clobber
   * values populated by an earlier step in the merge pipeline.
   */
  private _initializePreviousCells(rows: Array<Row>, numCols: number): void {
    if (!rows) return;
    for (const row of rows) {
      if (!row.previousCells || row.previousCells.length !== numCols) {
        row.previousCells = Array.from({ length: numCols }, () => ({ value: undefined } as Cell));
      }
      if (row.children && row.children.length) {
        this._initializePreviousCells(row.children, numCols);
      }
    }
  }

  /**
   * Seed template-row previousCells from each STANDARD DataFrame's
   * lineItemValues. Used as a base for rows that won't have a meaningful
   * rollup (formula/calculated leaf rows). Parent rows that have children
   * will be overwritten by the rollup pass that runs after this.
   */
  private _seedTemplatePreviousCellsFromLineItemValues(
    orderedPayloads: Array<PreviousPeriodPayload>,
    templateRowsByLineItemId: Map<number, Row>,
  ): void {
    const valueLookups = orderedPayloads.map(p => {
      const m = new Map<number, number | undefined>();
      for (const v of p.lineItemValues || []) {
        m.set(Number(v.lineItemId), v.value);
      }
      return m;
    });

    templateRowsByLineItemId.forEach((row, lid) => {
      if (!row.previousCells) return;
      valueLookups.forEach((lookup, idx) => {
        const v = lookup.get(lid);
        if (v != null) {
          row.previousCells[idx].value = v;
        }
      });
    });
  }

  /**
   * Top-level previous-period tree walk. Mirrors `calculate()` (the
   * active-cell flow) but operates on previousCells with a parallel
   * calculator-ref dict so formula/calculated rows are evaluated
   * against the prior period's values. Includes the same forward-
   * reference retry loop calculate() uses.
   */
  private _calculatePreviousPeriodTree(): void {
    if (!this.previousPeriodColumns || this.previousPeriodColumns.length === 0) return;
    if (!this.rows) return;

    const prevCalcRef: Record<string, Array<number>> = {};
    const orderedRowRefs: Array<string> = [];
    let deferredCalcRows: Array<Row> = [];

    for (const r of this.rows) {
      this._calculatePreviousPeriodRow(r, deferredCalcRows, orderedRowRefs, prevCalcRef);
    }

    let retry = 0;
    while (retry < MAX_RECALC_ATTEMPTS && deferredCalcRows.length) {
      retry++;
      const rowsToIter = deferredCalcRows;
      deferredCalcRows = [];
      for (const r of rowsToIter) {
        this._calculatePreviousPeriodRow(r, deferredCalcRows, orderedRowRefs, prevCalcRef);
      }
    }

    if (deferredCalcRows.length) {
      deferredCalcRows.forEach(r => {
        console.error(`Failed to evaluate prev-period equation ${r.formula}`);
      });
    }
  }

  /**
   * Mirrors calculateRow for previousCells. Either rolls up from
   * children or evaluates a formula against the prev-period calculator
   * ref. After processing, registers the row's ref so subsequent
   * formula rows can reference its prev-period value.
   */
  private _calculatePreviousPeriodRow(
    row: Row,
    deferredCalcRows: Array<Row>,
    orderedRowRefs: Array<string>,
    prevCalcRef: Record<string, Array<number>>,
  ): void {
    if (!row.template) return;

    const hasRealChildren = !!(row.children && row.children.length);
    const hasPriorOnlyChildren = !!(row.previousOnlyChildren && row.previousOnlyChildren.length);

    if (hasRealChildren) {
      for (const c of row.children) {
        this._calculatePreviousPeriodRow(c, deferredCalcRows, orderedRowRefs, prevCalcRef);
      }
    }
    if (hasRealChildren || hasPriorOnlyChildren) {
      // Synths in previousOnlyChildren are leaves with previousCells
      // already populated by _spliceInPriorOnlySourceRows, so no
      // recursion is needed before the rollup.
      this._rollupPreviousCellsOnRow(row);
    } else if (row.formula) {
      try {
        this._evaluatePreviousPeriodFormula(row, prevCalcRef, orderedRowRefs);
      } catch (err) {
        if (err instanceof DeferredCalculationError && deferredCalcRows) {
          deferredCalcRows.push(row);
          return;
        }
        throw err;
      }
    }

    if (row.ref && row.previousCells) {
      // Mirrors the active path in calculateRow: rollupCoefficient===0
      // means "validation only" (raw value passed through), otherwise
      // run through getCellValueForCalculations so row.invertValue is
      // applied. Without the inversion, formulas referencing inverted
      // rows get the wrong sign in prev-period columns.
      prevCalcRef[row.ref] = row.previousCells.map(c =>
        row.rollupCoefficient === 0 ? c.value : this.getCellValueForCalculations(row, c)
      );
      orderedRowRefs.push(row.ref);
    }

    // Mirror calculateRow's validation step: flag/offBy on previousCells
    // are what the template reads for the colour indication and offBy
    // subtext on prev-period columns.
    if (row.validationRow) {
      this._validatePreviousPeriodRow(row);
    } else if (row.lineItemId === STANDARD_ITEM_IDS.BALANCE_CHECK) {
      this._validatePreviousPeriodCheckRow(row);
    }
  }

  /**
   * Mirrors validateRow for previousCells. Compares each prev cell on
   * the row to the corresponding prev cell on row.validationRow and
   * sets flag / flagReason / offBy. validationRow.previousCells is
   * populated upstream by _populatePreviousCellsOnSourceRows since the
   * validation row is itself a source row in state.sourceRows.
   */
  private _validatePreviousPeriodRow(row: Row): void {
    if (!row.previousCells || !row.validationRow) return;
    row.previousCells.forEach((cell, idx) => {
      const validationCell = row.validationRow.previousCells?.[idx];
      const expectedValue = this._getPreviousValidationValue(row.validationRow, validationCell, idx);
      // Treat a missing prior value as 0 for the comparison, but do NOT
      // mutate cell.value — leaving it null lets the renderer show the
      // "—" placeholder instead of "0.00".
      const cv = row.rollupCoefficient === 0
        ? (cell.value == null ? 0.0 : cell.value)
        : this.getCellValueForCalculations(row, cell);

      if (typeof expectedValue !== 'number' || typeof cv !== 'number') {
        cell.flag = true;
        cell.flagReason = 'Validation or value is not a number';
        cell.offBy = 0.0;
      } else if (expectedValue.toFixed(2) !== cv.toFixed(2)) {
        const offBy = cv - expectedValue;
        cell.flag = true;
        cell.flagReason = `Off by ${offBy.toFixed(2)}`;
        cell.offBy = offBy;
      } else {
        cell.flag = false;
        cell.flagReason = '';
        cell.offBy = 0.0;
      }
    });
  }

  /**
   * Signed prior-period value of a validation source row. Like the rollup,
   * the current spread's invert toggle must not flip a read-only prior
   * column, so the sign comes from _shouldInvertPreviousValue rather than
   * getCellValueForCalculations (which reads the live row.invertValue).
   */
  private _getPreviousValidationValue(validationRow: Row, cell: Cell | undefined, cellIdx: number): number {
    if (!cell || !cell.value) return 0.0;
    const value = parseFloat(cell.value as any);
    return this._shouldInvertPreviousValue(validationRow, cellIdx) ? -value : value;
  }

  /** Mirrors validateCheckRow for previousCells (BALANCE_CHECK only). */
  private _validatePreviousPeriodCheckRow(row: Row): void {
    if (!row.previousCells) return;
    row.previousCells.forEach(cell => {
      if (cell.value == null) {
        cell.flag = false;
        cell.flagReason = '';
        cell.offBy = 0.0;
        return;
      }
      if (Math.abs(cell.value) > 1.0) {
        cell.flag = true;
        cell.flagReason = `Off by ${cell.value.toFixed(2)}`;
        cell.offBy = -cell.value;
      } else {
        cell.flag = false;
        cell.flagReason = '';
        cell.offBy = 0.0;
      }
    });
  }

  /**
   * Mirrors evaluateRowFormula but operates on previousCells using the
   * parallel prev-period calculator-ref dict. PRIOR(...) references
   * are skipped (value=0) the same way the active path does.
   */
  private _evaluatePreviousPeriodFormula(
    row: Row,
    prevCalcRef: Record<string, Array<number>>,
    orderedRowRefs: Array<string>,
  ): void {
    if (!row.previousCells || !row.formula) return;
    const equation = row.formula.startsWith('=') ? row.formula.substring(1) : row.formula;
    const equationRefs = this.extractVariablesFromEquation(equation);
    this.checkForOutOfOrderRefs(equationRefs, orderedRowRefs);

    row.previousCells.forEach((c, idx) => {
      let value = 0;
      const scope = this.equationScopeForColumn(idx, prevCalcRef);
      if (!equation.toLowerCase().includes('PRIOR('.toLowerCase())) {
        try {
          value = math.evaluate(equation, scope);
        } catch (e) {
          throw new DeferredCalculationError(`failed to eval prev-period equation: ${equation}`);
        }
      }
      c.value = value;
    });
  }

  /**
   * Mirrors calculateRollupsForRow for previousCells: sums each child's
   * prev cell into the parent's prev cell at the same column index,
   * applying rollupCoefficient and invertValue. Single-row, non-recursive
   * (the recursion lives in _calculatePreviousPeriodRow).
   *
   * Sums across both real children (current-spread source rows / nested
   * template rows) and previousOnlyChildren (synth prior-only rows).
   * The two are kept in separate slots so synths never contaminate
   * active-period rollups, but for the prev-period sum they're equal
   * contributors.
   *
   * If no child contributed a value at a given index, the parent's
   * existing value at that index is preserved (which may be the seed
   * from _seedTemplatePreviousCellsFromLineItemValues, or undefined).
   * Matches active-cell behavior: rollups overwrite when children
   * provide values; otherwise the seeded base remains.
   */
  private _rollupPreviousCellsOnRow(row: Row): void {
    if (!row.previousCells) return;
    const realChildren = row.children || [];
    const priorChildren = row.previousOnlyChildren || [];
    if (realChildren.length === 0 && priorChildren.length === 0) return;

    const allChildren = [...realChildren, ...priorChildren];
    const numCols = row.previousCells.length;
    const rollupCoefficients = allChildren.map(c => (c.rollupCoefficient == null ? 1 : c.rollupCoefficient));

    for (let idx = 0; idx < numCols; idx++) {
      let anyChildHadValue = false;
      let sum = 0;
      allChildren.forEach((c, ci) => {
        const v = c.previousCells?.[idx]?.value;
        if (v == null) return;
        anyChildHadValue = true;
        const signed = this._shouldInvertPreviousValue(c, idx) ? -v : v;
        sum += signed * rollupCoefficients[ci];
      });
      if (anyChildHadValue) {
        row.previousCells[idx].value = sum;
      }
    }
  }

  /**
   * Previous-period columns are read-only, so a user's invert toggle on a
   * current-spread source row must not flip their sign. Source rows and
   * prior-only rows instead honor the inversion recorded on the prior
   * spread's own source row; template rows use their template invertValue.
   */
  private _shouldInvertPreviousValue(row: Row, cellIdx: number): boolean {
    if (row.template) return !!row.invertValue;
    return !!row.previousCells?.[cellIdx]?.wasInvertedInPriorSpread;
  }

  private _spliceInPriorOnlySourceRows(
    orderedPayloads: Array<PreviousPeriodPayload>,
    templateRowsByLineItemId: Map<number, Row>,
  ): void {
    // Keys of the current source rows already categorized, qualified by
    // occurrence so N duplicate prior rows need N duplicate current rows to be
    // fully covered. Built with the same keyer that
    // _populatePreviousCellsOnSourceRows looks up with, so the two always agree
    // on which prior rows are already handled.
    const currentKeys = new Set<string>();
    const currentCounts = new Map<string, number>();
    for (const sr of this.sourceRows || []) {
      if (sr.header) continue;
      // Uncategorized rows arrive as categorizedTo 0, not null.
      if (!sr.categorizedTo) continue;
      currentKeys.add(nextSourceRowKey(currentCounts, sr.categorizedTo, sr.userFacingLabel || sr.label));
    }

    // For each prior payload, walk its source rows.
    orderedPayloads.forEach((payload, payloadIdx) => {
      const priorCounts = new Map<string, number>();
      for (const priorRow of payload.sourceRows || []) {
        if (priorRow.header) continue;
        const lid = priorRow.categorizedToLineItemId;
        if (!lid) continue;
        const key = nextSourceRowKey(priorCounts, lid, priorRow.label);
        if (currentKeys.has(key)) {
          // A current source row covers this row — handled in
          // _populatePreviousCellsOnSourceRows.
          continue;
        }
        const tplRow = templateRowsByLineItemId.get(lid);
        if (!tplRow) continue;

        // Reuse synthetic rows we created earlier in this loop iteration
        // for the same (lineItemId, label, occurrence) so the same row carries
        // cells from multiple prior periods. The synth may live in either the
        // previousOnlyChildren list or the validationRow slot — check both.
        const synthKey = `__prior__:${key}`;
        const isValidationSynth = priorRow.categorizationType === 'validation';
        let synthRow: Row | undefined = (tplRow.previousOnlyChildren || []).find(c => c.uuid === synthKey);
        if (!synthRow && tplRow.validationRow && tplRow.validationRow.isPreviousOnlyRow && tplRow.validationRow.uuid === synthKey) {
          synthRow = tplRow.validationRow;
        }
        if (!synthRow) {
          synthRow = {
            uuid: synthKey,
            label: priorRow.label || '',
            categorizedTo: lid,
            categorizationType: priorRow.categorizationType || 'component',
            isPreviousOnlyRow: true,
            labelIsLocked: true,
            cells: [],
            previousCells: orderedPayloads.map(() => ({ value: undefined })),
            indentation: (tplRow.indentation || 0) + 1,
            itemClass: priorRow.itemClass,
            invertValue: priorRow.invertValue,
            rollupCoefficient: 1,
            textFormat: priorRow.textFormat,
          };
          // Promote a validation-typed synth into the parent's validationRow
          // slot when no current validation row exists. This preserves the
          // prev-period validation flagging on the parent's previousCells
          // (via _validatePreviousPeriodRow) and keeps the validation source
          // row visible (it renders via the always-on <tr *ngIf="row.validationRow">
          // block, with empty active cells and populated prev cells).
          // Active-cell validateRow short-circuits on isPreviousOnlyRow so the
          // synth doesn't spuriously flag the active columns.
          if (isValidationSynth && !tplRow.validationRow) {
            tplRow.validationRow = synthRow;
          } else {
            if (!tplRow.previousOnlyChildren) tplRow.previousOnlyChildren = [];
            tplRow.previousOnlyChildren.push(synthRow);
          }
        }
        synthRow.previousCells![payloadIdx] = {
          value: priorRow.value == null ? undefined : priorRow.value,
          sourceComments: priorRow.sourceComments,
          wasInvertedInPriorSpread: !!priorRow.invertValue,
        };
      }
    });
  }

  private _populatePreviousCellsOnSourceRows(orderedPayloads: Array<PreviousPeriodPayload>): void {
    if (!this.sourceRows || this.sourceRows.length === 0) return;

    // Build a (lineItemId, normalizedLabel, occurrence) -> Cell[] lookup from
    // prior payloads. The occurrence suffix gives each duplicate label its own
    // slot instead of every duplicate overwriting a shared one.
    const cellsByKey = new Map<string, Array<Cell>>();
    orderedPayloads.forEach((payload, payloadIdx) => {
      const priorCounts = new Map<string, number>();
      for (const priorRow of payload.sourceRows || []) {
        if (priorRow.header) continue;
        const lid = priorRow.categorizedToLineItemId;
        // Uncategorized rows arrive as categorizedToLineItemId 0, not null.
        if (!lid) continue;
        const k = nextSourceRowKey(priorCounts, lid, priorRow.label);
        let arr = cellsByKey.get(k);
        if (!arr) {
          arr = orderedPayloads.map(() => ({ value: undefined } as Cell));
          cellsByKey.set(k, arr);
        }
        arr[payloadIdx] = {
          value: priorRow.value == null ? undefined : priorRow.value,
          sourceComments: priorRow.sourceComments,
          wasInvertedInPriorSpread: !!priorRow.invertValue,
        };
      }
    });

    const currentCounts = new Map<string, number>();
    for (const sr of this.sourceRows) {
      const lid = sr.header ? null : sr.categorizedTo;
      if (!lid) {
        sr.previousCells = orderedPayloads.map(() => ({ value: undefined }));
        continue;
      }
      const k = nextSourceRowKey(currentCounts, lid, sr.userFacingLabel || sr.label);
      const cells = cellsByKey.get(k);
      sr.previousCells = cells
        ? cells.map(c => ({ ...c }))
        : orderedPayloads.map(() => ({ value: undefined }));
    }
  }

}
